import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { SuperAdminSkip } from "@/components/site/SuperAdminSkip";
import { createPortal } from "react-dom";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { goldConfetti } from "@/lib/confetti";
import { randomFamily } from "@/lib/test-data";

import { focusFormAnchor, reportFormError } from "@/lib/form-feedback";
import {
  consumeEnrollmentPrefillFlag,
  ENROLLMENT_PREFILL_EVENT,
} from "@/lib/guide-prefill";

import {
  CheckboxField,
  ComboboxField,
  DateField,
  Field,
  FieldError,
  SelectField,
  PhoneField,
  TextField,
  TextareaField,
  type Option,
} from "@/components/site/fields";
import { isValidPhone } from "@/lib/phone";
import { Input } from "@/components/ui/input";
import { BadgePercent, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { GUARDIAN_RELATIONS, enrollmentSchemaForLocale } from "@/lib/enrollment-schema";
import { submitEnrollment } from "@/lib/enrollment.functions";
import {
  trackEnrollmentSaved,
  trackEnrollmentStart,
  trackEnrollmentSubmit,
  visitorFields,
} from "@/lib/analytics-client";
import { saveEnrollmentDraft, touchEnrollmentDraft } from "@/lib/enrollment-draft.functions";
import { draftProgress } from "@/lib/enrollment-progress";
import { countryOptions } from "@/lib/countries";
import { countryNameIn } from "@/lib/format";
import { AddressFields } from "@/components/site/AddressFields";
import { PageModal } from "@/components/site/PageModal";
import { PrivacyPolicyContent, TermsPolicyContent } from "@/components/site/EnrollmentPolicyContent";
import { FeesByTerm } from "@/components/site/FeesByTerm";
import { postalCodeError } from "@/lib/address";
import { getDebugModeForAdmin } from "@/lib/settings.functions";
import {
  GRADE_OPTIONS,
  PERIODS,
  TERMS,
  activityFeeForTerm,
  afterschoolPricesForTerm,
  bookableTerms,
  calculateFees,
  footballAcademyEligibility,
  afterschoolEligibility,
  padelEligibility,
  formatEur,
  registrationDueText,
  isShortPeriod,
  familySiblingDiscounts,
  siblingDiscountFromChild,
  percentForChildNumber,

  type FeeLineInfo,
  type FeeResult,
  type PeriodId,
  type TermId,
  applySitePricing,
} from "@/lib/fees";
import { sitePricingQuery } from "@/lib/pricing-query";
import {
  formatSwedishPersonalNumber,
  sanitizeSwedishPersonalNumberInput,
  genderFromSwedishPersonalNumber,

  idHintForNationality,
  idLabelForNationality,
  idPlaceholderForNationality,
  idTypeForNationality,
  toIsoDate,
  validateIdNumber,
  validateSwedishPersonalNumber,
} from "@/lib/identity";
import { formatPassportNumber, validatePassportNumber } from "@/lib/passport";
import { suggestPlacement } from "@/lib/placement";

import {
  checkStartDate,
  earliestStartDate,
  firstDayFor,
  getTerm,
  lastDayFor,
  shortStayEndDate,
} from "@/lib/schoolYear";
import { pathFor } from "@/lib/i18n";
import { feeLabelIn } from "@/lib/enrollment-i18n";
import {
  ENROLLMENT_TEXT,
  GRADE_LABEL_EN,
  GRADE_LABEL_ES,
  NATIONALITY_GROUP_LABEL_EN,
  NATIONALITY_GROUP_LABEL_ES,
  NATIONALITY_LABEL_EN,
  NATIONALITY_LABEL_ES,
  PERIOD_LABEL_EN,
  PERIOD_LABEL_ES,
  RELATION_LABEL_EN,
  RELATION_LABEL_ES,
  TERM_LABEL_EN,
  TERM_LABEL_ES,
  type EnrollmentLocale,
} from "@/lib/i18n/enrollment";

const DRAFT_KEY = "enrollment-draft-v7";
/**
 * Utkastets id ligger separat från innehållet, så att en omladdning eller ett
 * tomt formulär inte skapar en ny påbörjad anmälan varje gång. Nytt id sätts
 * bara vid inskickad anmälan eller "Rensa och börja om".
 */
const DRAFT_ID_KEY = "enrollment-draft-id-v1";

function readDraftId(): string {
  try {
    const saved = window.localStorage.getItem(DRAFT_ID_KEY);
    if (saved && /^[0-9a-f-]{36}$/i.test(saved)) return saved;
    const fresh = crypto.randomUUID();
    window.localStorage.setItem(DRAFT_ID_KEY, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

function newDraftId(): string {
  const fresh = crypto.randomUUID();
  try {
    window.localStorage.setItem(DRAFT_ID_KEY, fresh);
  } catch {
    /* localStorage kan vara avstängt */
  }
  return fresh;
}

type Student = {
  studentFirstName: string;
  studentLastName: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  gender: string;
  noIdNumber: boolean;
  idNumber: string;
  idCountry: string;
  passportNumber: string;
  passportCountry: string;
  nationality: string;
  address: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
  sameAddressAs: string;
  previousSchool: string;
  sharesHealthInformation: boolean;
  notes: string;
  healthNote: string;
  startTerm: TermId;
  grade: string;
  gradeTouched: boolean;
  period: PeriodId;
  startDate: string;
  customStartDate: boolean;
  endDate: string;
  /** Antal veckor respektive månader för korttid. */
  shortCount: string;
  wantsAfterschool: boolean;
  wantsFootballAcademy: boolean;
  wantsPadelTennis: boolean;
  specialNeeds: boolean;
  specialNeedsNote: string;
};

type Guardian = {
  firstName: string;
  lastName: string;
  relation: string;
  email: string;
  phone: string;
  sameAddressAs: string;
  address: string;
  postalCode: string;
  city: string;
  region: string;
  country: string;
  nationality: string;
  noIdNumber: boolean;
  idNumber: string;
  passportNumber: string;
  passportCountry: string;
};

const emptyStudent: Student = {
  studentFirstName: "",
  studentLastName: "",
  birthYear: "",
  birthMonth: "",
  birthDay: "",
  gender: "",
  noIdNumber: false,
  idNumber: "",
  idCountry: "",
  passportNumber: "",
  passportCountry: "",
  nationality: "",
  address: "",
  postalCode: "",
  city: "",
  region: "",
  country: "",
  sameAddressAs: "own",
  previousSchool: "",
  sharesHealthInformation: false,
  notes: "",
  healthNote: "",
  startTerm: "HT26",
  grade: "",
  gradeTouched: false,
  period: "term",
  startDate: "",
  customStartDate: false,
  endDate: "",
  shortCount: "1",
  wantsAfterschool: false,
  wantsFootballAcademy: false,
  wantsPadelTennis: false,
  specialNeeds: false,
  specialNeedsNote: "",
};

const emptyGuardian: Guardian = {
  firstName: "",
  lastName: "",
  relation: "Vårdnadshavare",
  email: "",
  phone: "",
  sameAddressAs: "0",
  address: "",
  postalCode: "",
  city: "",
  region: "",
  country: "",
  nationality: "",
  noIdNumber: false,
  idNumber: "",
  passportNumber: "",
  passportCountry: "",
};

type SectionState = "empty" | "started" | "done";

function stateOf(values: string[], required: boolean[]): SectionState {
  const filled = values.filter((v) => v.trim() !== "").length;
  if (filled === 0) return "empty";
  const missing = required.some((ok) => !ok);
  return missing ? "started" : "done";
}

/**
 * Platt karta över ifyllda fält, används bara för att kunna visa i vilken
 * ordning och när familjen fyllde i utkastet. Inga extra uppgifter samlas in.
 */
function flattenDraftFields(
  students: Student[],
  guardians: Guardian[],
  consent: boolean,
  wantsConsultation: boolean,
): Record<string, string> {
  const out: Record<string, string> = {};
  const put = (prefix: string, obj: Record<string, unknown>) => {
    for (const [key, value] of Object.entries(obj)) {
      if (value == null) continue;
      if (typeof value === "string") {
        if (value.trim() !== "") out[`${prefix}${key}`] = value.trim();
      } else if (typeof value === "boolean") {
        if (value) out[`${prefix}${key}`] = "ja";
      } else if (typeof value === "number") {
        out[`${prefix}${key}`] = String(value);
      }
    }
  };
  students.forEach((s, i) => put(`students.${i}.`, s as unknown as Record<string, unknown>));
  guardians.forEach((g, i) => put(`guardians.${i}.`, g as unknown as Record<string, unknown>));
  if (consent) out["consent"] = "ja";
  if (wantsConsultation) out["wantsConsultation"] = "ja";
  return out;
}



function sectionStatusOf(students: Student[], guardians: Guardian[], consent: boolean) {
  const s = students[0];
  const g = guardians[0];
  return {
    student: stateOf(
      [s?.studentFirstName ?? "", s?.studentLastName ?? "", s?.nationality ?? "", s?.idNumber ?? ""],
      [
        Boolean(s?.studentFirstName),
        Boolean(s?.studentLastName),
        Boolean(s?.nationality),
        Boolean(s?.birthYear),
      ],
    ),
    schooling: stateOf(
      [s?.grade ?? "", s?.startTerm ?? "", s?.period ?? ""],
      [Boolean(s?.grade), Boolean(s?.startTerm), Boolean(s?.period)],
    ),
    guardians: stateOf(
      [g?.firstName ?? "", g?.lastName ?? "", g?.email ?? "", g?.phone ?? ""],
      [Boolean(g?.firstName), Boolean(g?.lastName), Boolean(g?.email), Boolean(g?.phone)],
    ),
    consent: (consent ? "done" : "empty") as SectionState,
  };
}

function hasDraftContent(
  students: Student[],
  guardians: Guardian[],
  consent: boolean,
  wantsConsultation: boolean,
): boolean {
  if (consent || wantsConsultation) return true;
  for (const s of students) {
    if (
      s.studentFirstName ||
      s.studentLastName ||
      s.idNumber ||
      s.passportNumber ||
      s.address ||
      s.postalCode ||
      s.city ||
      s.previousSchool ||
      s.notes ||
      s.healthNote ||
      s.gradeTouched ||
      s.customStartDate ||
      s.wantsAfterschool ||
      s.wantsFootballAcademy ||
      s.wantsPadelTennis
    ) {
      return true;
    }
  }
  for (const g of guardians) {
    if (
      g.firstName ||
      g.lastName ||
      g.email ||
      g.phone ||
      g.idNumber ||
      g.passportNumber ||
      g.address ||
      g.postalCode ||
      g.city
    ) {
      return true;
    }
  }
  return false;
}

const dayOptions: Option[] = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

/**
 * Dagens datum i skolans tidszon. Servern kör i UTC och besökaren i Madrid –
 * utan gemensam tidszon skiljer sig sidan mellan server och webbläsare under
 * kvällstimmarna, vilket ger renderingsfel.
 */
export function schoolTodayIso(): string {
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

const CURRENT_YEAR = Number(schoolTodayIso().slice(0, 4));
/** Äldsta årskullen i listan – man kan ha börjat skolan senare. */
const MIN_BIRTH_YEAR = CURRENT_YEAR - 22;
const yearOptions: Option[] = Array.from({ length: CURRENT_YEAR - MIN_BIRTH_YEAR + 1 }, (_, i) => {
  const y = CURRENT_YEAR - i;
  return { value: String(y), label: String(y) };
});

/** Ett år från ett ifyllt personnummer kan ligga utanför listan – lägg till det ändå. */
function yearOptionsWith(value: string): Option[] {
  if (!value || yearOptions.some((o) => o.value === value)) return yearOptions;
  return [{ value, label: value }, ...yearOptions];
}

/** Ålder vid årets slut, för att kunna varna om eleven är för gammal eller för ung. */
function ageWarning(birthYear: string, locale: EnrollmentLocale): string | null {
  const y = Number(birthYear);
  if (!y) return null;
  const age = CURRENT_YEAR - y;
  if (age >= 3 && age <= 18) return null;
  if (age > 18) {
    return locale === "en"
      ? `The date of birth gives an age of ${age} years – the school admits pupils aged 3–18. Please check that this is the child's ID number.`
      : locale === "es"
        ? `La fecha de nacimiento indica ${age} años: el colegio admite alumnos de 3 a 18 años. Comprueba que sea el documento del niño o la niña.`
        : `Födelsedatumet ger åldern ${age} år – skolan tar emot elever 3–18 år. Kontrollera att det är barnets ID-nummer.`;
  }
  return locale === "en"
    ? "The child is younger than 3 years – contact the school."
    : locale === "es"
      ? "El niño o la niña tiene menos de 3 años: contacta con el colegio."
      : "Barnet är yngre än 3 år – kontakta skolan.";
}


function relationOptionsFor(locale: EnrollmentLocale): Option[] {
  return GUARDIAN_RELATIONS.map((r) => ({
    value: r,
    label:
      locale === "en" ? (RELATION_LABEL_EN[r] ?? r) : locale === "es" ? (RELATION_LABEL_ES[r] ?? r) : r,
  }));
}
function termOptionsFor(locale: EnrollmentLocale): Option[] {
  return bookableTerms().map((t) => ({ value: t.id, label: termLabelFor(locale, t.id) }));
}
function periodOptionsFor(locale: EnrollmentLocale): Option[] {
  return PERIODS.map((p) => ({
    value: p.id,
    label: locale === "en" ? PERIOD_LABEL_EN[p.id] : locale === "es" ? PERIOD_LABEL_ES[p.id] : p.label,
  }));
}
function gradeOptionsFor(locale: EnrollmentLocale): Option[] {
  return GRADE_OPTIONS.map((g) => ({
    value: g.value,
    label:
      locale === "en"
        ? (GRADE_LABEL_EN[g.value] ?? g.label)
        : locale === "es"
          ? (GRADE_LABEL_ES[g.value] ?? g.label)
          : g.label,
  }));
}
function nationalityOptionsFor(locale: EnrollmentLocale): Option[] {
  const options = countryOptions();
  if (locale === "sv") return options;
  const labels = locale === "en" ? NATIONALITY_LABEL_EN : NATIONALITY_LABEL_ES;
  const groups = locale === "en" ? NATIONALITY_GROUP_LABEL_EN : NATIONALITY_GROUP_LABEL_ES;
  return options.map((o) => {
    const next: Option = { ...o, label: labels[o.value] ?? o.label };
    if (o.group) next.group = groups[o.group] ?? o.group;
    return next;
  });
}
function termLabelFor(locale: EnrollmentLocale, id: TermId): string {
  if (locale === "en") return TERM_LABEL_EN[id];
  if (locale === "es") return TERM_LABEL_ES[id];
  return TERMS.find((t) => t.id === id)?.label ?? id;
}
function idLabelFor(locale: EnrollmentLocale, nationality: string): string {
  if (locale === "sv") return idLabelForNationality(nationality);
  const type = idTypeForNationality(nationality);
  if (locale === "es") {
    if (type === "se") return "Número personal sueco";
    if (type === "es") return "NIE / DNI";
    return "Número de identificación / pasaporte";
  }
  if (type === "se") return "Personal number";
  if (type === "es") return "NIE / DNI";
  return "ID / passport number";
}

/** Kort etikett i kryssrutan – aldrig "ID-nummer / passnummer" med snedstreck. */
function missingIdLabel(locale: EnrollmentLocale, nationality: string): string {
  const type = idTypeForNationality(nationality);
  if (locale === "es") {
    if (type === "se") return "número personal sueco";
    if (type === "es") return "NIE / DNI";
    return "número de identificación";
  }
  if (locale === "en") {
    if (type === "se") return "personal number";
    if (type === "es") return "NIE / DNI";
    return "ID number";
  }
  if (type === "se") return "personnummer";
  if (type === "es") return "NIE / DNI";
  return "ID-nummer";
}

function Section({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-soft sm:p-8">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold">{title}</h3>
          {description ? (
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : <span />}
      </div>
      <div className="mt-6 min-w-0 space-y-5">{children}</div>
    </section>
  );
}

function AddRowButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 px-4 py-4 text-sm font-semibold text-primary transition-colors hover:border-primary hover:bg-primary/10 active:bg-primary/15"
    >
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function AddressSummary({
  values,
  title,
  emptyText,
}: {
  values: { address: string; postalCode: string; city: string; region: string; country: string };
  title: string;
  emptyText: string;
}) {
  const line1 = values.address.trim();
  const line2 = [values.postalCode, values.city].filter(Boolean).join(" ").trim();
  const line3 = [values.region, values.country].filter(Boolean).join(", ").trim();
  const empty = !line1 && !line2 && !line3;
  return (
    <div className="rounded-lg border border-border bg-sand/60 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {empty ? (
        <p className="mt-1 text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <address className="mt-1 space-y-0.5 text-sm not-italic leading-snug">
          {line1 ? <span className="block">{line1}</span> : null}
          {line2 ? <span className="block">{line2}</span> : null}
          {line3 ? <span className="block text-muted-foreground">{line3}</span> : null}
        </address>
      )}
    </div>
  );
}

function isFilledBirth(s: Student) {
  return Boolean(s.birthYear && s.birthMonth && s.birthDay);
}

function studentBirthDate(s: Student) {
  return isFilledBirth(s) ? toIsoDate(s.birthYear, s.birthMonth, s.birthDay) : "";
}

function studentGrade(s: Student) {
  const birthDate = studentBirthDate(s);
  const placement = birthDate ? suggestPlacement(birthDate, s.startTerm) : null;
  const grade = s.gradeTouched && s.grade ? s.grade : (placement?.grade ?? "");
  const option = GRADE_OPTIONS.find((g) => g.value === grade);
  return { placement, grade, program: option?.program ?? null };
}

export function EnrollmentForm({ locale = "sv" }: { locale?: EnrollmentLocale } = {}) {
  const submit = useServerFn(submitEnrollment);
  // Samma fråga som sidan förladdar på servern – annars saknas priser i första
  // bildrutan och formuläret varnar felaktigt för "inte beslutad".
  const { data: sitePricing, refetch: refetchPricing } = useQuery(sitePricingQuery);
  applySitePricing(sitePricing?.pricing ?? null, sitePricing?.termSettings ?? null);
  const t = ENROLLMENT_TEXT[locale];
  const relationOptions = relationOptionsFor(locale);
  const termOptions = termOptionsFor(locale);
  const periodOptions = periodOptionsFor(locale);
  const gradeOptions = gradeOptionsFor(locale);
  const nationalityOptions = nationalityOptionsFor(locale);
  const genderOptions: Option[] = t.genderOptions as unknown as Option[];
  const monthOptions: Option[] = t.months.map((m, i) => ({
    value: String(i + 1),
    label: m,
  }));
  /** Dagens datum – styr att passerade terminsstarter ersätts med snarast möjliga skoldag. */
  const todayIso = useMemo(() => schoolTodayIso(), []);
  const suggestedLabelText =
    locale === "en" ? "Suggested" : locale === "es" ? "Sugerida" : "Föreslagen";
  const asapLabelText =
    locale === "en" ? "As soon as possible" : locale === "es" ? "Lo antes posible" : "Snarast";


  const [students, setStudents] = useState<Student[]>([{ ...emptyStudent }]);
  const [guardians, setGuardians] = useState<Guardian[]>([{ ...emptyGuardian }]);
  const [consent, setConsent] = useState(false);
  const [healthConsent, setHealthConsent] = useState(false);
  const [wantsConsultation, setWantsConsultation] = useState(false);
  const [website, setWebsite] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** Vilka ID-fält som har lämnats – styr när felmeddelandet får visas. */
  const [touchedIds, setTouchedIds] = useState<Record<string, boolean>>({});
  /** Fält som användaren själv angett – används av utkast/förifyllning. */
  const [, setUserSet] = useState<Record<string, true>>({});
  /** Passlandet följer nationaliteten – väljaren visas bara när den efterfrågas. */
  const [showPassportCountry, setShowPassportCountry] = useState<Record<string, boolean>>({});


  const [sending, setSending] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<
    { kind: "student" | "guardian"; index: number } | null
  >(null);
  const [infoTopic, setInfoTopic] = useState<
    { topic: "afterschool" | "football" | "padel"; term: string } | null
  >(null);
  // Flyttgenomgångens "Läs mer"-modal.
  const [relocationInfoOpen, setRelocationInfoOpen] = useState(false);
  // Intern sida (integritetspolicy, villkor, prislista) öppnad i modal på plats.
  const [pageModal, setPageModal] = useState<{ href: string; title: string } | null>(null);
  const openPage = (href: string, title: string) => setPageModal({ href, title });
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [showRestoredDraft, setShowRestoredDraft] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Räknare som triggar "fortsätt där du slutade" efter att utkastet har renderats.
  const [continueTick, setContinueTick] = useState(0);
  const [guidePrefilled, setGuidePrefilled] = useState(false);



  useEffect(() => {
    let active = true;
    // Anropas bara när någon faktiskt är inloggad – annars svarar servern 401.
    void (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data } = await supabase.auth.getSession();
        if (!active || !data.session) return;
        const v = await getDebugModeForAdmin();
        if (active) setDebugMode(Boolean(v?.enabled));
      } catch {
        /* debugläget är valfritt */
      }
    })();
    return () => {
      active = false;
    };
  }, []);
  const [done, setDone] = useState(false);
  const [submittedNames, setSubmittedNames] = useState<string[]>([]);
  const formTop = useRef<HTMLDivElement | null>(null);
  const restored = useRef(false);
  const draftId = useRef<string>("");
  const lastRemote = useRef<string>("");
  const fieldSnapshot = useRef<Record<string, string>>({});
  const fieldEvents = useRef<Record<string, { key: string; firstAt: string; lastAt: string }>>({});

  const saveDraft = useServerFn(saveEnrollmentDraft);
  const touchDraft = useServerFn(touchEnrollmentDraft);

  // Hämta tillbaka påbörjat utkast
  useEffect(() => {
    if (restored.current) return;
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw) as {
          students?: Student[];
          guardians?: Guardian[];
          consent?: boolean;
          healthConsent?: boolean;
          wantsConsultation?: boolean;
          savedAt?: string;
          draftId?: string;
        };
        // Utkast utan riktigt innehåll (bara standardvärden, t.ex. ett gammalt
        // förifyllt land) är skräp – släng det i stället för att återställa.
        if (!hasDraftContent(draft.students ?? [], draft.guardians ?? [], draft.consent ?? false, draft.wantsConsultation ?? false)) {
          window.localStorage.removeItem(DRAFT_KEY);
          restored.current = true;
          if (!draftId.current) draftId.current = readDraftId();
          trackEnrollmentStart(draftId.current);
          if (consumeEnrollmentPrefillFlag()) setGuidePrefilled(true);
          return;
        }
        const restoredStudents = draft.students?.length
          ? draft.students.map((s) => ({ ...emptyStudent, ...s }))
          : null;
        const restoredGuardians = draft.guardians?.length
          ? draft.guardians.map((g) => ({ ...emptyGuardian, ...g }))
          : null;
        if (restoredStudents) setStudents(restoredStudents);
        if (restoredGuardians) setGuardians(restoredGuardians);
        // Ett återställt utkast har användaren själv fyllt i – räkna fälten som användarangivna.
        if (restoredStudents || restoredGuardians) {
          markAllUserSet(restoredStudents ?? students, restoredGuardians ?? guardians);
        }
        if (typeof draft.consent === "boolean") setConsent(draft.consent);
        if (typeof draft.healthConsent === "boolean") setHealthConsent(draft.healthConsent);
        if (typeof draft.wantsConsultation === "boolean") {
          setWantsConsultation(draft.wantsConsultation);
        }
        if (draft.savedAt) setSavedAt(draft.savedAt);
        // Äldre utkast bar id:t i själva paketet – flytta över det en gång.
        if (draft.draftId) {
          draftId.current = draft.draftId;
          try {
            if (!window.localStorage.getItem(DRAFT_ID_KEY)) {
              window.localStorage.setItem(DRAFT_ID_KEY, draft.draftId);
            }
          } catch {
            /* localStorage kan vara avstängt */
          }
        }
        if (hasDraftContent(draft.students ?? [], draft.guardians ?? [], draft.consent ?? false, draft.wantsConsultation ?? false)) {
          setShowRestoredDraft(true);
          toast.info(t.draftRestored, { id: "draft-restored" });
          // Familjen ska hamna direkt på nästa ruta de behöver fylla i.
          setContinueTick((n) => n + 1);
        }
      }
    } catch {
      /* ignorera trasigt utkast */
    }
    if (!draftId.current) draftId.current = readDraftId();
    restored.current = true;
    // Spåras först här, så händelsen kan kopplas till rätt påbörjad anmälan.
    trackEnrollmentStart(draftId.current);
    if (consumeEnrollmentPrefillFlag()) setGuidePrefilled(true);
  }, []);

  // AI-guiden kan fylla i utkastet medan formuläret redan är öppet
  useEffect(() => {
    const onPrefill = (event: Event) => {
      const draft = (event as CustomEvent<{ students?: Student[]; guardians?: Guardian[] }>).detail;
      const nextStudents = draft?.students?.length
        ? draft.students.map((s) => ({ ...emptyStudent, ...s }))
        : null;
      const nextGuardians = draft?.guardians?.length
        ? draft.guardians.map((g) => ({ ...emptyGuardian, ...g }))
        : null;
      if (nextStudents) setStudents(nextStudents);
      if (nextGuardians) setGuardians(nextGuardians);
      if (nextStudents || nextGuardians) {
        markAllUserSet(nextStudents ?? students, nextGuardians ?? guardians);
      }
      setGuidePrefilled(true);
      consumeEnrollmentPrefillFlag();
    };
    window.addEventListener(ENROLLMENT_PREFILL_EVENT, onPrefill);
    return () => window.removeEventListener(ENROLLMENT_PREFILL_EVENT, onPrefill);
  }, []);

  // Spara löpande i webbläsaren – men bara när familjen faktiskt fyllt i något.
  // Ett helt tomt formulär (t.ex. efter "Rensa och börja om") lämnar inget
  // utkast kvar, så andra sidor inte påstår att en anmälan pågår.
  useEffect(() => {
    if (!restored.current || done) return;
    if (!hasDraftContent(students, guardians, consent, wantsConsultation)) {
      try {
        window.localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* localStorage kan vara avstängt */
      }
      return;
    }
    const savedAtIso = new Date().toISOString();
    try {
      window.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          students,
          guardians,
          consent,
          healthConsent,
          wantsConsultation,
          savedAt: savedAtIso,
          draftId: draftId.current,
        }),
      );
      setSavedAt(savedAtIso);
    } catch {
      /* localStorage kan vara avstängt */
    }
  }, [students, guardians, consent, healthConsent, wantsConsultation, done]);

  // Autospara till databasen var femte sekund när något har ändrats
  useEffect(() => {
    if (done) return;
    const timer = window.setInterval(() => {
      if (!restored.current || !draftId.current) return;
      const payload = JSON.stringify({ students, guardians, consent, healthConsent, wantsConsultation });
      if (payload === lastRemote.current) return;
      const first = students[0];
      const guardian = guardians[0];
      const studentName = `${first?.studentFirstName ?? ""} ${first?.studentLastName ?? ""}`.trim();
      const guardianName = `${guardian?.firstName ?? ""} ${guardian?.lastName ?? ""}`.trim();
      if (!studentName && !guardianName) return;
      lastRemote.current = payload;
      const sectionStatus = sectionStatusOf(students, guardians, consent);

      // Ifyllnadsordning: bara fältnamn och tidpunkt, aldrig extra uppgifter.
      const now = new Date().toISOString();
      const flat = flattenDraftFields(students, guardians, consent, wantsConsultation);
      for (const [key, value] of Object.entries(flat)) {
        const previous = fieldSnapshot.current[key];
        const event = fieldEvents.current[key];
        if (!event) fieldEvents.current[key] = { key, firstAt: now, lastAt: now };
        else if (previous !== value) event.lastAt = now;
      }
      fieldSnapshot.current = flat;

      trackEnrollmentSaved("", draftId.current, draftProgress(sectionStatus).percent);
      void saveDraft({
        data: {
          draftId: draftId.current,
          payload: JSON.stringify({
            students,
            guardians,
            consent,
            healthConsent,
            wantsConsultation,
            fieldEvents: Object.values(fieldEvents.current),
          }),
          sectionStatus,
          studentName,
          guardianName,
          guardianEmail: guardian?.email ?? "",
          guardianPhone: guardian?.phone ?? "",
          studentsCount: students.length,
          locale,
        },
      }).catch(() => {
        lastRemote.current = "";
      });

    }, 5000);
    return () => window.clearInterval(timer);
  }, [students, guardians, consent, healthConsent, wantsConsultation, done, locale, saveDraft]);

  // Livssignal: talar om att formuläret är öppet, även om inget ändras.
  useEffect(() => {
    if (done) return;
    const ping = () => {
      if (!restored.current || !draftId.current) return;
      if (document.visibilityState !== "visible") return;
      void touchDraft({ data: { draftId: draftId.current } }).catch(() => {});
    };
    ping();
    const timer = window.setInterval(ping, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", ping);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", ping);
    };
  }, [done, touchDraft]);

  // Flyttgenomgången gäller bara heltid (termin/helår), inte korttid.
  const hasFullTimeStudent = students.some((s) => !isShortPeriod(s.period));

  // Om bara korttid är valt ska genomgången inte vara ibockad.
  useEffect(() => {
    if (!hasFullTimeStudent && wantsConsultation) setWantsConsultation(false);
  }, [hasFullTimeStudent, wantsConsultation]);

  // Vid termin/helår startar man automatiskt när terminen startar.
  useEffect(() => {
    setStudents((list) => {
      let changed = false;
      const next = list.map((s) => {
        if (isShortPeriod(s.period)) {
          // Korttid: tidigast en vecka fram, inom vald termin och på en öppen skoldag.
          const program = studentGrade(s).program ?? "";
          const floor = earliestStartDate(todayIso, s.startTerm, program);
          if (!floor || (s.startDate && s.startDate >= floor)) return s;
          if (s.startDate === floor) return s;
          changed = true;
          return { ...s, startDate: floor };
        }
        if (s.customStartDate) return s;
        const program = studentGrade(s).program ?? "";
        const first = firstDayFor(s.startTerm, program) ?? "";
        // Har terminsstarten passerat sätts tidigast möjliga skoldag i stället.
        const earliest = earliestStartDate(todayIso, s.startTerm, program);
        const target = first && earliest > first ? earliest : first;
        if (s.startDate === target) return s;
        changed = true;
        return { ...s, startDate: target };
      });
      return changed ? next : list;
    });
  }, [students, todayIso]);

  // Korttid: slutdatum räknas ut från startdatum + antal veckor/månader.
  useEffect(() => {
    setStudents((list) => {
      let changed = false;
      const next = list.map((s) => {
        if (!isShortPeriod(s.period)) {
          if (!s.endDate) return s;
          changed = true;
          return { ...s, endDate: "" };
        }
        const program = studentGrade(s).program ?? "";
        const valid = s.startDate && checkStartDate(s.startTerm, s.startDate, program).ok;
        const end = valid
          ? (shortStayEndDate(
              s.startTerm,
              s.startDate,
              s.period === "short_week" ? "week" : "month",
              Number(s.shortCount) || 1,
              program,
            ).endDate ?? "")
          : "";
        if (s.endDate === end) return s;
        changed = true;
        return { ...s, endDate: end };
      });
      return changed ? next : list;
    });
  }, [students]);

  // Fortsätt där man slutade: rulla till första tomma obligatoriska rutan.
  useEffect(() => {
    if (continueTick === 0) return;
    const timer = window.setTimeout(() => {
      focusFormAnchor(firstIncompleteAnchor() ?? "enrollment-consent", { prefer: "empty" });
    }, 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueTick]);


  /** Markerar att användaren själv angett fält (skiljer från automatiskt förifyllda standardvärden). */
  function markUserSet(prefix: string, index: number, patch: Record<string, unknown>) {
    setUserSet((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) next[`${prefix}${index}.${key}`] = true;
      return next;
    });
  }

  function updateStudent(index: number, patch: Partial<Student>) {
    markUserSet("s", index, patch);
    clearFieldErrors(`students.${index}`, Object.keys(patch));
    setStudents((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function updateGuardian(index: number, patch: Partial<Guardian>) {
    markUserSet("g", index, patch);
    clearFieldErrors(`guardians.${index}`, Object.keys(patch));
    setGuardians((list) => list.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  }

  /** Tar bort röd felmarkering för de fält användaren just ändrat. */
  function clearFieldErrors(prefix: string, keys: string[]) {
    setErrors((prev) => {
      let next: Record<string, string> | null = null;
      for (const key of keys) {
        const full = `${prefix}.${key}`;
        if (full in prev) {
          next ??= { ...prev };
          delete next[full];
        }
      }
      return next ?? prev;
    });
  }

  /** Markerar alla fält som användarangivna – används vid utkast, testdata och Sol-förifyllning. */
  function markAllUserSet(nextStudents: Student[], nextGuardians: Guardian[]) {
    setUserSet(() => {
      const next: Record<string, true> = {};
      // Bara fält som faktiskt skiljer sig från tomma standardvärdet räknas som
      // ifyllda – annars visar ett tomt utkast en falsk ifyllnadsgrad.
      nextStudents.forEach((s, i) => {
        for (const [key, value] of Object.entries(s)) {
          if (key === "customStartDate") continue;
          if (value === (emptyStudent as Record<string, unknown>)[key]) continue;
          next[`s${i}.${key}`] = true;
        }
      });
      nextGuardians.forEach((g, i) => {
        for (const [key, value] of Object.entries(g)) {
          if (value === (emptyGuardian as Record<string, unknown>)[key]) continue;
          next[`g${i}.${key}`] = true;
        }
      });
      return next;
    });
  }

  /** Elever kan dela adress med elev 1 – vi löser upp kedjan här. */
  function resolvedAddress(index: number) {
    const student = students[index] ?? students[0]!;
    const source = student.sameAddressAs === "own" ? student : (students[0] ?? student);
    return {
      address: source.address,
      postalCode: source.postalCode,
      city: source.city,
      region: source.region,
      country: source.country,
    };
  }

  /**
   * Födelsedatum och kön ur ett giltigt personnummer. Körs både när man skriver
   * och när fältet lämnas (då numret nyss normaliserats med bindestreck).
   */
  function derivedFromPersonalNumber(value: string, current: Student): Partial<Student> {
    const patch: Partial<Student> = {};
    const result = validateSwedishPersonalNumber(value);
    if (!result.valid) return patch;
    const [y, m, d] = result.birthDate.split("-");
    const year = Number(y);
    if (year >= MIN_BIRTH_YEAR && year <= CURRENT_YEAR) {
      patch.birthYear = y!;
      patch.birthMonth = String(Number(m));
      patch.birthDay = String(Number(d));
    } else {
      // Året ligger utanför skolans åldrar – fyll inte i datumet,
      // formuläret visar en förklarande text i stället.
      patch.birthYear = "";
      patch.birthMonth = "";
      patch.birthDay = "";
    }
    // Kön kan läsas ur ett giltigt personnummer (inte samordningsnummer).
    // Vi fyller bara i när familjen inte redan valt något själva.
    if (!current.gender) {
      const gender = genderFromSwedishPersonalNumber(value);
      if (gender) patch.gender = gender;
    }
    return patch;
  }

  /** Medan man skriver: rensa bara ogiltiga tecken, formatera inte om. */
  function handleIdNumber(index: number, value: string) {
    const student = students[index]!;
    const isSe = idTypeForNationality(student.nationality) === "se";
    const next = isSe ? sanitizeSwedishPersonalNumberInput(value) : value;
    const patch: Partial<Student> = { idNumber: next };
    if (isSe) Object.assign(patch, derivedFromPersonalNumber(next, student));
    updateStudent(index, patch);
  }

  /** När fältet lämnas: städa upp ett komplett personnummer till ÅÅÅÅMMDD-NNNN. */
  function handleIdNumberBlur(index: number) {
    const student = students[index]!;
    if (idTypeForNationality(student.nationality) !== "se") return;
    const formatted = formatSwedishPersonalNumber(student.idNumber);
    const patch: Partial<Student> = {
      ...derivedFromPersonalNumber(formatted, student),
      ...(formatted !== student.idNumber ? { idNumber: formatted } : {}),
    };
    if (Object.keys(patch).length) updateStudent(index, patch);
    setTouchedIds((prev) => ({ ...prev, [`student-${index}`]: true }));
  }



  const perStudent = useMemo(() => {
    // Först skolavgiften per barn – syskonrabatten läggs enligt skolsystemets
    // regel på de billigaste barnen, inte på barnet som råkar ligga sist.
    const basics = students.map((s) => {
      const { placement, grade, program } = studentGrade(s);
      const academy = footballAcademyEligibility(program, s.period, grade);
      const afterschool = afterschoolEligibility(program, s.period);
      const padel = padelEligibility(program, s.period);
      const base: FeeResult | null = program
        ? calculateFees({
            program,
            period: s.period,
            term: s.startTerm,
            afterschool: s.wantsAfterschool && afterschool.allowed,
            footballAcademy: s.wantsFootballAcademy && academy.allowed,
            padelTennis: s.wantsPadelTennis && padel.allowed,
            childrenCount: 1,
            grade,
            applyDate: new Date(`${todayIso}T12:00:00Z`),
            startDate: s.startDate || null,
            shortStayCount: Number(s.shortCount),
          })
        : null;
      return {
        placement,
        grade,
        program,
        academy,
        afterschool,
        padel,
        schoolFee: base?.lines?.[0]?.amount ?? 0,
      };
    });
    const siblingShares = familySiblingDiscounts(basics.map((b) => b.schoolFee));

    return students.map((s, i) => {
      const b = basics[i]!;
      const { placement, grade, program, academy, afterschool, padel } = b;
      const share = siblingShares[i];
      const fees: FeeResult | null = program
        ? calculateFees({
            program,
            period: s.period,
            term: s.startTerm,
            afterschool: s.wantsAfterschool && afterschool.allowed,
            footballAcademy: s.wantsFootballAcademy && academy.allowed,
            padelTennis: s.wantsPadelTennis && padel.allowed,
            childrenCount: students.length,
            siblingDiscount: share ?? null,
            grade,
            applyDate: new Date(`${todayIso}T12:00:00Z`),
            startDate: s.startDate || null,
            shortStayCount: Number(s.shortCount),
          })
        : null;

        const idIsSe = idTypeForNationality(s.nationality) === "se";
        // Svenska personnummer felmarkeras först när fältet lämnats.
        const showIdError = !idIsSe || touchedIds[`student-${i}`] === true;
        const idError =
          s.noIdNumber || !s.idNumber || !showIdError
            ? ""
            : (() => {
                const check = validateIdNumber(
                  idTypeForNationality(s.nationality),
                  s.idNumber,
                  studentBirthDate(s) || undefined,
                  locale,
                );
                return check.valid ? "" : (check.error ?? t.invalidId);
              })();


        const startDateError = s.startDate
          ? (checkStartDate(s.startTerm, s.startDate, program ?? "").reason ?? "")
          : "";
        const short = isShortPeriod(s.period)
          ? shortStayEndDate(
              s.startTerm,
              startDateError ? "" : s.startDate,
              s.period === "short_week" ? "week" : "month",
              Number(s.shortCount) || 1,
              program ?? "",
            )
          : {};
        return {
          placement,
          grade,
          program,
          fees,
          idError,
          startDateError,
          academy,
          afterschool,
          padel,
          siblingDiscount: share ?? null,
          shortEndDate: short.endDate ?? "",
          shortError: short.error ? t.shortStayOutsideTerm : "",
        };
    });
  }, [students, touchedIds]);


  const grandTotal = perStudent.reduce((sum, s) => sum + (s.fees?.total ?? 0), 0);
  const feePending = perStudent.some((s) => s.fees?.pricingPending);
  const hasHealthInformation = students.some(
    (s) => s.specialNeeds || s.specialNeedsNote.trim() || s.sharesHealthInformation || (s.healthNote ?? "").trim(),
  );

  /** Ifyllnadsgrad över alla obligatoriska fält – växer när fler barn läggs till. */
  const progress = useMemo(() => {
    const steps: { label: string; done: boolean }[] = [];
    students.forEach((s, i) => {
      const who = students.length > 1 ? `${t.studentSectionTitleMulti(i + 1)}: ` : "";
      steps.push({ label: `${who}${t.firstName}`, done: Boolean(s.studentFirstName.trim()) });
      steps.push({ label: `${who}${t.lastName}`, done: Boolean(s.studentLastName.trim()) });
      // Födelsedatumsteget räknas bara när fälten faktiskt frågas.
      if (Boolean(s.nationality) && (idTypeForNationality(s.nationality) !== "se" || s.noIdNumber)) {
        steps.push({ label: `${who}${t.birthYear}`, done: isFilledBirth(s) });
      }
      // Endast obligatoriska fält räknas, och ett fält är klart så snart det har
      // ett värde – även när värdet är förvalt.
      steps.push({
        label: `${who}${t.nationality}`,
        done: Boolean(s.nationality.trim()),
      });
      steps.push({
        label: `${who}ID`,
        done: s.noIdNumber ? Boolean(s.passportNumber.trim() && s.passportCountry.trim()) : Boolean(s.idNumber.trim()),
      });
      steps.push({ label: `${who}${t.desiredStart}`, done: Boolean(s.startTerm) });
      steps.push({ label: `${who}${t.period}`, done: Boolean(s.period) });
      steps.push({ label: `${who}${t.gradeLevel}`, done: Boolean(studentGrade(s).grade) });
      steps.push({ label: `${who}${t.gender}`, done: Boolean(s.gender.trim()) });
      if (isShortPeriod(s.period)) {
        steps.push({ label: `${who}${t.startDate}`, done: Boolean(s.startDate.trim()) });
        
      }

      if (s.sameAddressAs === "own") {
        steps.push({ label: `${who}${t.addressLabel}`, done: Boolean(s.country.trim()) });
        steps.push({ label: `${who}${t.addressLabel}`, done: Boolean(s.address.trim()) });
        steps.push({ label: `${who}${t.addressLabel}`, done: Boolean(s.postalCode.trim()) });
        steps.push({ label: `${who}${t.addressLabel}`, done: Boolean(s.city.trim()) });
      }
    });
    guardians.forEach((g, i) => {
      const who = guardians.length > 1 ? `${t.guardianLabel(i + 1)}: ` : "";
      steps.push({ label: `${who}${t.firstName}`, done: Boolean(g.firstName.trim()) });
      steps.push({ label: `${who}${t.lastName}`, done: Boolean(g.lastName.trim()) });
      steps.push({ label: `${who}${t.email}`, done: Boolean(g.email.trim()) });
      steps.push({ label: `${who}${t.phone}`, done: Boolean(g.phone.trim()) });
      steps.push({ label: `${who}${t.relationToStudent}`, done: Boolean(g.relation.trim()) });
      steps.push({ label: `${who}${t.nationality}`, done: Boolean(g.nationality.trim()) });
      steps.push({
        label: `${who}ID`,
        done: g.noIdNumber
          ? Boolean(g.passportNumber.trim() && g.passportCountry.trim())
          : Boolean(g.idNumber.trim()),
      });
      if (g.sameAddressAs === "own") {
        steps.push({ label: `${who}${t.addressForGuardian}`, done: Boolean(g.country.trim()) });
        steps.push({ label: `${who}${t.addressForGuardian}`, done: Boolean(g.address.trim()) });
        steps.push({ label: `${who}${t.addressForGuardian}`, done: Boolean(g.postalCode.trim()) });
        steps.push({ label: `${who}${t.addressForGuardian}`, done: Boolean(g.city.trim()) });
      }
    });

    steps.push({ label: t.consentSectionTitle, done: consent });
    if (hasHealthInformation) {
      steps.push({ label: t.healthConsentShort, done: healthConsent });
    }
    const done = steps.filter((s) => s.done).length;
    const next = steps.find((s) => !s.done)?.label ?? "";
    return { pct: Math.round((done / Math.max(steps.length, 1)) * 100), next };
  }, [students, guardians, consent, healthConsent, hasHealthInformation, t]);



  /**
   * Samlar ALLA fel i formuläret i ett svep, i samma ordning som fälten ligger
   * på sidan (elevkort uppifrån och ner, sedan vårdnadshavare). Första felet i
   * den ordningen är det som toastas och scrollas till – aldrig ett fält längre
   * ner före ett fält längre upp.
   */
  function collectManualErrors(): Record<string, string> {
    const map: Record<string, string> = {};
    const put = (key: string, message: string) => {
      if (!map[key]) map[key] = message;
    };

    students.forEach((s, i) => {
      const info = perStudent[i]!;
      const p = `students.${i}`;
      // Visuell ordning i kortet: nationalitet → id/pass → födelsedatum → kön
      // (zod) → adress → skolgång (termin, period, årskurs, datum).
      if (!s.nationality) put(`${p}.nationality`, t.toastChooseNationality(i + 1));
      if (s.noIdNumber) {
        if (!s.passportNumber.trim()) put(`${p}.passportNumber`, t.toastPassport(i + 1));
        if (!s.passportCountry.trim()) put(`${p}.passportCountry`, t.toastPassport(i + 1));
        if (s.passportNumber.trim()) {
          const pass = validatePassportNumber(s.passportCountry, s.passportNumber, locale);
          if (!pass.valid) put(`${p}.passportNumber`, pass.error ?? t.invalidId);
        }
      } else {
        if (!s.idNumber.trim()) {
          put(`${p}.idNumber`, t.toastEnterIdOrCheck(idLabelFor(locale, s.nationality), i + 1));
        } else if (info.idError) {
          setTouchedIds((prev) => ({ ...prev, [`student-${i}`]: true }));
          put(`${p}.idNumber`, info.idError);
        }
      }
      // Födelsedatum frågas bara när det inte går att läsa ur personnumret –
      // annars pekar felet på ett fält som inte finns på sidan.
      const birthAsked =
        Boolean(s.nationality) &&
        (idTypeForNationality(s.nationality) !== "se" || s.noIdNumber);
      if (birthAsked && !isFilledBirth(s)) {
        const msg = t.toastFillBirthDate(i + 1);
        if (!s.birthYear) put(`${p}.birthYear`, msg);
        if (!s.birthMonth) put(`${p}.birthMonth`, msg);
        if (!s.birthDay) put(`${p}.birthDay`, msg);
      }
      const postalError = postalCodeError(s.country, s.postalCode, locale);
      if (s.sameAddressAs === "own" && postalError) {
        put(`${p}.postalCode`, t.toastPostalError(i + 1, postalError));
      }
      // Saknat pris stoppar inte anmälan – familjen får besked om plats och pris.
      // Blockera bara när själva skolvalet saknas.
      if (!info.program) put(`${p}.grade`, t.toastChooseGrade(i + 1));
      if (info.startDateError) put(`${p}.startDate`, info.startDateError);
      if (isShortPeriod(s.period)) {
        if (info.shortError) {
          put(`${p}.endDate`, info.shortError);
        } else {
          if (!s.startDate) put(`${p}.startDate`, t.toastChooseShortDates(i + 1));
          if (!s.endDate) put(`${p}.endDate`, t.toastChooseShortDates(i + 1));
          if (s.startDate && s.endDate && s.endDate < s.startDate) {
            put(`${p}.endDate`, t.toastEndAfterStart(i + 1));
          }
        }
      }
    });

    guardians.forEach((g, i) => {
      const p = `guardians.${i}`;
      if (g.phone.trim() && !isValidPhone(g.phone)) put(`${p}.phone`, t.toastGuardianPhone(i + 1));
      if (!g.nationality) put(`${p}.nationality`, t.toastGuardianNationality(i + 1));
      if (g.noIdNumber) {
        if (!g.passportNumber.trim()) put(`${p}.passportNumber`, t.toastGuardianPassport(i + 1));
        if (!g.passportCountry.trim()) put(`${p}.passportCountry`, t.toastGuardianPassport(i + 1));
        if (g.passportNumber.trim()) {
          const pass = validatePassportNumber(g.passportCountry, g.passportNumber, locale);
          if (!pass.valid) {
            put(`${p}.passportNumber`, t.toastGuardianIdInvalid(i + 1, pass.error ?? t.invalidId));
          }
        }
      } else {
        if (!g.idNumber.trim()) {
          put(`${p}.idNumber`, t.toastGuardianId(i + 1, idLabelFor(locale, g.nationality)));
        } else {
          const check = validateIdNumber(
            idTypeForNationality(g.nationality),
            g.idNumber,
            undefined,
            locale,
          );
          if (!check.valid) {
            setTouchedIds((prev) => ({ ...prev, [`guardian-${i}`]: true }));
            put(`${p}.idNumber`, t.toastGuardianIdInvalid(i + 1, check.error ?? t.invalidId));
          }
        }
      }
    });

    return map;
  }

  /**
   * Fältordning exakt som på sidan: elev 1 (personuppgifter, adress, skolgång),
   * elev 2 …, vårdnadshavare 1 …, samtycke. Används för att välja vilket fel
   * som ska toastas och scrollas till först.
   */
  function visualErrorOrder(): string[] {
    const order: string[] = [];
    students.forEach((_s, i) => {
      const p = `students.${i}`;
      order.push(
        `${p}.studentFirstName`, `${p}.studentLastName`,
        `${p}.nationality`, `${p}.idNumber`, `${p}.passportNumber`, `${p}.passportCountry`,
        `${p}.birthYear`, `${p}.birthMonth`, `${p}.birthDay`,
        `${p}.gender`,
        `${p}.address`, `${p}.postalCode`, `${p}.city`, `${p}.region`, `${p}.country`,
        `${p}.startTerm`, `${p}.period`, `${p}.grade`, `${p}.startDate`, `${p}.endDate`,
      );
    });
    guardians.forEach((_g, i) => {
      const p = `guardians.${i}`;
      order.push(
        `${p}.firstName`, `${p}.lastName`, `${p}.email`, `${p}.phone`, `${p}.relation`,
        `${p}.nationality`, `${p}.idNumber`, `${p}.passportNumber`, `${p}.passportCountry`,
        `${p}.address`, `${p}.postalCode`, `${p}.city`, `${p}.region`, `${p}.country`,
      );
    });
    order.push("consent", "healthConsent");
    return order;
  }

  /** Visar alla fel rödmarkerade och scrollar till det översta felaktiga fältet. */
  function reportAllErrors(map: Record<string, string>) {
    setErrors(map);
    const firstKey =
      visualErrorOrder().find((key) => map[key]) ?? Object.keys(map)[0];
    const message = (firstKey ? map[firstKey] : undefined) ?? t.toastCheckFields;
    const [root, idx] = firstKey?.split(".") ?? [];
    const anchor =
      root === "students" && idx !== undefined
        ? `student-${idx}`
        : root === "guardians" && idx !== undefined
          ? `guardian-${idx}`
          : firstKey === "consent" || firstKey === "healthConsent"
            ? "enrollment-consent"
            : "enrollment-form";
    reportFormError(message, { anchorId: anchor, toastId: "enrollment-validation" });
  }

  /**
   * Första sektionen där ett obligatoriskt fält fortfarande är tomt, i samma
   * ordning som man fyller i: elev 1, elev 2 …, vårdnadshavare, samtycke.
   * Inga fel visas – detta används bara för att fortsätta där man slutade.
   */
  function firstIncompleteAnchor(): string | null {
    for (let i = 0; i < students.length; i++) {
      const s = students[i]!;
      const ownAddress = s.sameAddressAs === "own";
      const missing =
        !s.studentFirstName.trim() ||
        !s.studentLastName.trim() ||
        !s.nationality.trim() ||
        (s.noIdNumber
          ? !s.passportNumber.trim() || !s.passportCountry.trim()
          : !s.idNumber.trim()) ||
        !isFilledBirth(s) ||
        !s.gender.trim() ||
        (ownAddress &&
          (!s.address.trim() ||
            !s.postalCode.trim() ||
            !s.city.trim() ||
            !s.country.trim())) ||
        !studentGrade(s).grade ||
        !s.startDate.trim();
      if (missing) return `student-${i}`;
    }
    for (let i = 0; i < guardians.length; i++) {
      const g = guardians[i]!;
      const missing =
        !g.firstName.trim() ||
        !g.lastName.trim() ||
        !g.email.trim() ||
        !g.phone.trim() ||
        !g.relation.trim() ||
        !g.nationality.trim() ||
        (g.noIdNumber
          ? !g.passportNumber.trim() || !g.passportCountry.trim()
          : !g.idNumber.trim());
      if (missing) return `guardian-${i}`;
    }
    if (!consent) return "enrollment-consent";
    if (hasHealthInformation && !healthConsent) return "enrollment-health-consent";
    return null;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    // Kunde prislistan inte laddas: försök igen tyst innan vi fortsätter, så vi
    // inte sparar en anmälan utan avgift i onödan.
    if (perStudent.some((p) => p.fees?.pricingLoadFailed)) {
      try {
        await refetchPricing();
      } catch {
        /* Anmälan går ändå att skicka in – skolan hör av sig med pris. */
      }
    }

    const manual = collectManualErrors();


    const payload = {
      students: students.map((s, i) => {
        const info = perStudent[i]!;
        const { grade, program } = info;
        const {
          gradeTouched: _gradeTouched,
          customStartDate: _customStartDate,
          noIdNumber,
          ...rest
        } = s;
        const addr = resolvedAddress(i);
        return {
          ...rest,
          address: addr.address,
          postalCode: addr.postalCode,
          city: addr.city,
          region: addr.region,
          country: addr.country,
          idType: noIdNumber ? ("none" as const) : idTypeForNationality(s.nationality),
          idNumber:
            noIdNumber
              ? ""
              : idTypeForNationality(s.nationality) === "se"
                ? formatSwedishPersonalNumber(s.idNumber)
                : s.idNumber,
          idCountry: idTypeForNationality(s.nationality) === "other" ? s.nationality : "",
          grade,
          program: program ?? "primary",
          wantsFootballAcademy:
            s.wantsFootballAcademy &&
            footballAcademyEligibility(program, s.period, grade).allowed,
          wantsAfterschool:
            s.wantsAfterschool && afterschoolEligibility(program, s.period).allowed,
          wantsPadelTennis: s.wantsPadelTennis && padelEligibility(program, s.period).allowed,
          specialNeeds: s.specialNeeds,
          specialNeedsNote: s.specialNeeds ? s.specialNeedsNote.trim() : "",
          sharesHealthInformation: s.sharesHealthInformation,
          notes: (s.notes ?? "").trim(),
          healthNote: s.sharesHealthInformation ? (s.healthNote ?? "").trim() : "",
          submittedFees: info.fees,
        };
      }),
      guardians: guardians.map((g) => {
        const source = g.sameAddressAs === "own" ? g : resolvedAddress(Number(g.sameAddressAs) || 0);
        return {
          ...g,
          address: source.address,
          postalCode: source.postalCode,
          city: source.city,
          region: source.region,
          country: source.country,
          idType: idTypeForNationality(g.nationality),
          idNumber:
            g.noIdNumber
              ? ""
              : idTypeForNationality(g.nationality) === "se"
                ? formatSwedishPersonalNumber(g.idNumber)
                : g.idNumber,
        };
      }),
      consent,
      healthConsent,
      wantsRelocationConsultation: wantsConsultation,
      locale,
      website,
    };

    // Alla fel – både egna kontroller och schemat – visas samtidigt, och vi
    // scrollar till det översta felaktiga fältet i formulärets egen ordning.
    const parsed = enrollmentSchemaForLocale(locale).safeParse(payload);
    const map: Record<string, string> = { ...manual };
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path.join(".");
        if (!map[key]) map[key] = issue.message;
      }
    }
    if (Object.keys(map).length > 0) {
      reportAllErrors(map);
      return;
    }


    setSending(true);
    try {
      await submit({ data: { ...parsed.data, draftId: draftId.current, ...visitorFields() } });
      setSubmittedNames(students.map((s) => `${s.studentFirstName} ${s.studentLastName}`.trim()).filter(Boolean));
      setDone(true);
      window.requestAnimationFrame(() => {
        formTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      try {
        window.localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* noop */
      }
      draftId.current = newDraftId();
      lastRemote.current = "";
      window.dispatchEvent(new CustomEvent("sis-enrollment-draft-changed"));
      trackEnrollmentSubmit({ students: students.length, locale });
      void goldConfetti();
      toast.dismiss();
    } catch (error) {
      console.error(error);
      toast.error(t.toastGenericError, { id: "enrollment" });
    } finally {
      setSending(false);
    }
  }

  /** Fyller formuläret med slumpad men giltig testdata. Skickar aldrig in. */
  function fillTestData(children: 1 | 2 | 3, nationality: "Sverige" | "Spanien", extra: boolean) {
    const family = randomFamily(children, nationality, extra);
    const nextStudents = family.students.map((s) => ({
      ...emptyStudent,
      ...s,
      gradeTouched: true,
    }));
    const nextGuardians = family.guardians.map((g) => ({ ...emptyGuardian, ...g }));
    setStudents(nextStudents);
    setGuardians(nextGuardians);
    markAllUserSet(nextStudents, nextGuardians);
    setConsent(true);
    setHealthConsent(nextStudents.some((s) => s.specialNeeds || s.sharesHealthInformation));
    toast.success(t.testDataFilled, { id: "enrollment" });
  }


  /** Nollställer till ett tomt barn men behåller adress och vårdnadshavare. */
  function startAnotherApplication() {
    const prev = students[0];
    setStudents([
      {
        ...emptyStudent,
        address: prev?.address ?? "",
        postalCode: prev?.postalCode ?? "",
        city: prev?.city ?? "",
        region: prev?.region ?? "",
        country: prev?.country ?? emptyStudent.country,
      },
    ]);
    setConsent(false);
    setHealthConsent(false);
    setErrors({});
    setSavedAt(null);
    // Nollställ elevens progress – vårdnadshavarens ifyllda uppgifter behålls.
    setUserSet((prev) => {
      const next: Record<string, true> = {};
      for (const key of Object.keys(prev)) if (!key.startsWith("s")) next[key] = true;
      return next;
    });
    setSubmittedNames([]);
    draftId.current = newDraftId();
    lastRemote.current = "";
    setDone(false);
    window.requestAnimationFrame(() => {
      formTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  const relocationHref = pathFor("/flyttgenomgang", locale);
  const privacyHref = pathFor("/integritetspolicy", locale);
  const termsHref = pathFor("/anvandarvillkor", locale);
  const feesHref = pathFor("/avgifter", locale);

  return (
    <div ref={formTop} className="scroll-mt-28">
      {done ? (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-8">
          <h3 className="text-xl font-semibold">{t.doneTitle}</h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {t.doneBody(submittedNames.join(", "))}{" "}
            <a className="text-primary link-quiet" href="mailto:rektor@sweintschool.com">
              rektor@sweintschool.com
            </a>
            .
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.doneNext}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button type="button" onClick={startAnotherApplication}>
              {t.doneAnother}
            </Button>
            <span className="text-xs text-muted-foreground">{t.doneAnotherHint}</span>
          </div>
        </div>
      ) : null}
    <form
      noValidate
      id="enrollment-form"
      onSubmit={handleSubmit}
      className={done ? "hidden" : "grid gap-8 lg:grid-cols-[1fr_20rem] lg:items-start"}
    >
      <div className="min-w-0 space-y-6">
        <p className="rounded-xl border border-border bg-sand px-4 py-3 text-sm leading-relaxed text-muted-foreground">
          {locale === "en"
            ? "Takes about 5 minutes. Your answers are saved as you go, so you can pause and continue later in the same browser."
            : locale === "es"
              ? "Se tarda unos 5 minutos. Las respuestas se guardan sobre la marcha, así que podéis pausar y continuar más tarde en el mismo navegador."
              : "Tar ungefär 5 minuter. Det du fyller i sparas löpande, så du kan pausa och fortsätta senare i samma webbläsare."}
        </p>

        {guidePrefilled ? (
          <p className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary">
            {locale === "en"
              ? "The guide has filled in what you told us in the chat. Check the details and complete the rest — nothing is submitted until you press send."
              : locale === "es"
                ? "El guía ha rellenado lo que nos contaste en el chat. Revisa los datos y completa el resto: no se envía nada hasta que pulses enviar."
                : "Guiden har fyllt i det du berättade i chatten. Kontrollera uppgifterna och fyll i resten – inget skickas in förrän du trycker på skicka."}
          </p>
        ) : null}


        {showRestoredDraft && savedAt && hasDraftContent(students, guardians, consent, wantsConsultation) ? (
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t.draftBannerTitle}</p>
              <p className="text-xs text-muted-foreground">
                {t.draftBannerBody(
                  new Date(savedAt).toLocaleTimeString(locale === "sv" ? "sv-SE" : "en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "Europe/Madrid",
                  }),
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              className="text-xs font-medium text-muted-foreground link-quiet hover:text-foreground"
            >
              {t.draftDiscard}
            </button>
          </div>
        ) : null}
        {debugMode ? (
          <div className="rounded-xl border border-dashed border-primary/60 p-3">
            <p className="text-xs font-medium text-primary">{t.fillTestData}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {([1, 2, 3] as const).map((n) => (
                <button
                  key={`se-${n}`}
                  type="button"
                  onClick={() => fillTestData(n, "Sverige", n > 1)}
                  className="rounded-md border border-primary/40 px-3 py-1.5 text-xs font-medium text-primary"
                >
                  {n} {n === 1 ? "barn" : "barn"} · personnummer
                </button>
              ))}
              {([1, 2] as const).map((n) => (
                <button
                  key={`es-${n}`}
                  type="button"
                  onClick={() => fillTestData(n, "Spanien", n > 1)}
                  className="rounded-md border border-primary/40 px-3 py-1.5 text-xs font-medium text-primary"
                >
                  {n} barn · NIE/DNI
                </button>
              ))}
            </div>
          </div>
        ) : null}


        {students.map((s, i) => {
          const info = perStudent[i]!;
          const term = getTerm(s.startTerm);
          const termFirst = firstDayFor(s.startTerm, info.program ?? "") ?? term?.firstDay ?? "";
          const termLast = lastDayFor(s.startTerm, info.program ?? "") ?? term?.lastDay ?? "";
          // Har terminsstarten passerat får familjen tidigast möjliga skoldag i stället.
          const earliestStart = earliestStartDate(todayIso, s.startTerm, info.program ?? "");
          // Ligger terminsstarten närmare än 15 dagar gäller tidigaste möjliga start.
          const termStartPassed = Boolean(termFirst) && earliestStart > termFirst;
          // Korttid kan tidigast starta en vecka fram, och aldrig före terminens
          // första öppna skoldag.
          const startFloor = isShortPeriod(s.period)
            ? earliestStart
            : termStartPassed || !termFirst
              ? earliestStart
              : termFirst;
          const gradeOptionsForStudent = info.placement?.grade
            ? gradeOptions.map((o) =>
                o.value === info.placement?.grade
                  ? { ...o, suggested: true, suggestedLabel: suggestedLabelText }
                  : o,
              )
            : gradeOptions;
          const seCheck =
            idTypeForNationality(s.nationality) === "se"
              ? validateSwedishPersonalNumber(s.idNumber)
              : null;

          const birthFromId = Boolean(seCheck?.valid);
          /** Personnumret är giltigt men året ligger utanför skolans åldrar. */
          const idYearOutOfRange =
            seCheck?.valid === true &&
            (Number(seCheck.birthDate.slice(0, 4)) < MIN_BIRTH_YEAR ||
              Number(seCheck.birthDate.slice(0, 4)) > CURRENT_YEAR);
          // Fälten växer fram: inget visas innan nationaliteten är vald, och
          // födelsedatum frågas bara när det inte går att läsa ur ID-numret.
          const hasNationality = Boolean(s.nationality.trim());
          const idIsSeNat = idTypeForNationality(s.nationality) === "se";
          const showBirthFields = hasNationality && (!idIsSeNat || s.noIdNumber);
          const passportCountryKey = `student-${i}`;
          const showPassportCountryField =
            showPassportCountry[passportCountryKey] === true ||
            (Boolean(s.passportCountry) && s.passportCountry !== s.nationality) ||
            (s.noIdNumber && !s.passportCountry.trim());

          return (
            <div key={i} id={`student-${i}`} className="space-y-6">
              <Section
                title={students.length > 1 ? t.studentSectionTitleMulti(i + 1) : t.studentSectionTitleSingle}
                description={t.studentSectionDescription}
                action={
                  i > 0 ? (
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
                      onClick={() => setPendingRemoval({ kind: "student", index: i })}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                      {t.removeStudent}
                    </button>
                  ) : undefined
                }
              >
                <div className="grid min-w-0 gap-5 sm:grid-cols-2">
                  <TextField
                    label={t.firstName}
                    required
                    value={s.studentFirstName}
                    onChange={(v) => updateStudent(i, { studentFirstName: v })}
                    error={errors[`students.${i}.studentFirstName`]}
                  />
                  <TextField
                    label={t.lastName}
                    required
                    value={s.studentLastName}
                    onChange={(v) => updateStudent(i, { studentLastName: v })}
                    error={errors[`students.${i}.studentLastName`]}
                  />
                </div>

                <div className="rounded-lg bg-sand p-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <ComboboxField
                      label={t.nationality}
                      required
                      value={s.nationality}
                      onChange={(v) => updateStudent(i, { nationality: v, idNumber: "" })}
                      options={nationalityOptions}
                      placeholder={t.chooseNationality}
                      searchPlaceholder={t.searchCountry}
                      hint={t.nationalityHint}
                      error={errors[`students.${i}.nationality`]}
                    />
                    {hasNationality && !s.noIdNumber ? (
                      <TextField
                        label={idLabelFor(locale, s.nationality)}
                        required
                        value={s.idNumber}
                        onChange={(v) => handleIdNumber(i, v)}
                        onBlur={() => handleIdNumberBlur(i)}
                        inputMode={idIsSeNat ? "numeric" : "text"}
                        error={info.idError || errors[`students.${i}.idNumber`]}
                        placeholder={idPlaceholderForNationality(s.nationality)}
                        labelTooltip={
                          idIsSeNat
                            ? t.swedishPersonalNumberHint
                            : idHintForNationality(s.nationality, locale)
                        }
                      />
                    ) : null}
                  </div>

                  {hasNationality && birthFromId && !idYearOutOfRange ? (
                    <p className="mt-3 text-xs font-medium text-primary">
                      {seCheck && seCheck.valid && seCheck.coordination
                        ? t.coordinationNumberOk
                        : t.personalNumberValid}
                    </p>
                  ) : null}


                  {hasNationality ? (
                    <div className="mt-4">
                      <CheckboxField
                        checked={s.noIdNumber}
                        onChange={(v) =>
                          updateStudent(i, {
                            noIdNumber: v,
                            idNumber: "",
                            ...(v && !s.passportCountry ? { passportCountry: s.nationality } : {}),
                          })
                        }
                        compact
                      >
                        {t.studentMissingIdCheckbox(missingIdLabel(locale, s.nationality))}
                      </CheckboxField>
                    </div>
                  ) : null}

                  {hasNationality && s.noIdNumber ? (
                    <div className="mt-4 rounded-lg border border-border bg-background p-4">
                      <p className="text-xs leading-relaxed text-muted-foreground">
                        {t.passportIntro}
                      </p>
                      <div className="mt-4 grid gap-5 sm:grid-cols-2">
                        <TextField
                          label={t.passportNumber}
                          required
                          value={s.passportNumber}
                          onChange={(v) =>
                            updateStudent(i, { passportNumber: formatPassportNumber(v) })
                          }
                          labelTooltip={t.passportNumberHint}
                          error={
                            errors[`students.${i}.passportNumber`] ??
                            (validatePassportNumber(s.passportCountry, s.passportNumber, locale)
                              .error || undefined)
                          }
                          warning={
                            validatePassportNumber(s.passportCountry, s.passportNumber, locale)
                              .hint || undefined
                          }
                        />

                        {showPassportCountryField ? (
                          <ComboboxField
                            label={t.passportCountry}
                            required
                            value={s.passportCountry}
                            onChange={(v) => updateStudent(i, { passportCountry: v })}
                            options={nationalityOptions}
                            placeholder={t.passportCountryPlaceholder}
                            searchPlaceholder={t.searchCountry}
                            error={errors[`students.${i}.passportCountry`]}
                          />
                        ) : (
                          <div className="flex flex-col justify-center gap-1">
                            <p className="text-sm">
                              {t.passportIssuedIn(countryNameIn(s.nationality, locale))}
                            </p>
                            <button
                              type="button"
                              className="self-start text-xs font-medium text-primary link-quiet hover:opacity-80"
                              onClick={() =>
                                setShowPassportCountry((prev) => ({
                                  ...prev,
                                  [passportCountryKey]: true,
                                }))
                              }
                            >
                              {t.passportOtherCountry}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}


                  {idYearOutOfRange && seCheck?.valid ? (
                    <div className="mt-5">
                      <FieldError>{ageWarning(seCheck.birthDate.slice(0, 4), locale)}</FieldError>
                    </div>
                  ) : showBirthFields ? (
                    <>
                      <div className="mt-5 grid gap-4 sm:grid-cols-3">
                        <SelectField
                          label={t.birthYear}
                          required
                          value={s.birthYear}
                          onChange={(v) => updateStudent(i, { birthYear: v })}
                          options={yearOptionsWith(s.birthYear)}
                          placeholder={t.year}
                          error={errors[`students.${i}.birthYear`]}
                        />
                        <SelectField
                          label={t.birthMonth}
                          required
                          value={s.birthMonth}
                          onChange={(v) => updateStudent(i, { birthMonth: v })}
                          options={monthOptions}
                          placeholder={t.month}
                          error={errors[`students.${i}.birthMonth`]}
                        />
                        <SelectField
                          label={t.birthDay}
                          required
                          value={s.birthDay}
                          onChange={(v) => updateStudent(i, { birthDay: v })}
                          options={dayOptions}
                          placeholder={t.day}
                          error={errors[`students.${i}.birthDay`]}
                        />
                      </div>
                      {ageWarning(s.birthYear, locale) ? (
                        <div className="mt-2">
                          <FieldError>{ageWarning(s.birthYear, locale)}</FieldError>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <SelectField
                    label={t.gender}
                    required
                    value={s.gender}
                    onChange={(v) => updateStudent(i, { gender: v })}
                    options={genderOptions}
                    placeholder={t.chooseGender}
                    error={errors[`students.${i}.gender`]}
                  />
                </div>

                {i > 0 ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium">{t.addressLabel}</p>
                    <button
                      type="button"
                      aria-pressed={s.sameAddressAs !== "own"}
                      onClick={() =>
                        updateStudent(i, {
                          sameAddressAs: s.sameAddressAs === "own" ? "0" : "own",
                        })
                      }
                      className={
                        s.sameAddressAs !== "own"
                          ? "shrink-0 rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                          : "shrink-0 rounded-full border border-border px-3 py-1 text-xs font-medium transition-colors hover:border-primary hover:text-primary"
                      }
                    >
                      {t.sameAsStudent1Short(
                        students[0]?.studentFirstName.trim() || t.studentFallback(1),
                      )}
                    </button>
                  </div>
                ) : null}
                {i > 0 && s.sameAddressAs !== "own" ? (
                  <AddressSummary
                    values={resolvedAddress(0)}
                    title={t.usedAddress}
                    emptyText={t.addressNotFilledYet}
                  />
                ) : null}
                {i === 0 || s.sameAddressAs === "own" ? (
                  <AddressFields
                    values={{
                      address: s.address,
                      postalCode: s.postalCode,
                      city: s.city,
                      region: s.region,
                      country: s.country,
                    }}
                    locale={locale}
                    onChange={(patch) => updateStudent(i, patch)}
                    errors={{
                      address: errors[`students.${i}.address`],
                      postalCode: errors[`students.${i}.postalCode`],
                      city: errors[`students.${i}.city`],
                      region: errors[`students.${i}.region`],
                      country: errors[`students.${i}.country`],
                    }}
                  />
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label={t.previousSchool}
                    hint={t.previousSchoolHint}
                    value={s.previousSchool}
                    onChange={(v) => updateStudent(i, { previousSchool: v })}
                  />
                </div>

                <CheckboxField
                  checked={s.specialNeeds}
                  onChange={(v) => updateStudent(i, { specialNeeds: v })}
                  hint={t.specialNeedsHint}
                >
                  {t.specialNeeds}
                </CheckboxField>
                {s.specialNeeds ? (
                  <TextareaField
                    label={t.specialNeedsNote}
                    value={s.specialNeedsNote}
                    onChange={(v) => updateStudent(i, { specialNeedsNote: v })}
                  />
                ) : null}



                <CheckboxField
                  checked={s.sharesHealthInformation}
                  onChange={(v) => updateStudent(i, { sharesHealthInformation: v, ...(v ? {} : { healthNote: "" }) })}
                  hint={t.notesHint}
                >
                  {t.shareHealthInformation}
                </CheckboxField>
                {s.sharesHealthInformation ? (
                  <TextareaField
                    label={t.notes}
                    hint={t.notesHint}
                    value={s.healthNote ?? ""}
                    onChange={(v) => updateStudent(i, { healthNote: v })}
                    error={errors[`students.${i}.healthNote`]}
                  />
                ) : null}
                <TextareaField
                  label={t.otherNotes}
                  hint={t.otherNotesHint}
                  value={s.notes}
                  onChange={(v) => updateStudent(i, { notes: v })}
                  error={errors[`students.${i}.notes`]}
                />
              </Section>

              <Section
                title={students.length > 1 ? t.schoolingSectionTitleMulti(i + 1) : t.schoolingSectionTitleSingle}
                description={t.schoolingSectionDescription}
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div id={`student-${i}-startTerm`} className="min-w-0 space-y-2">
                    <SelectField
                      label={t.desiredStart}
                      required
                      value={s.startTerm}
                      onChange={(v) => updateStudent(i, { startTerm: v as TermId, startDate: "" })}
                      options={termOptions}
                      placeholder={t.chooseTerm}
                      hint={t.termHint}
                    />
                    {info.fees?.pricingPending ? (
                      <p className="rounded-lg bg-sand p-3 text-xs leading-relaxed text-muted-foreground">
                        {info.fees.pricingLoadFailed
                          ? t.feeLoadFailedBody
                          : t.feePendingForTerm(
                              students.length > 1
                                ? `${t.studentSectionTitleMulti(i + 1)}${s.studentFirstName ? ` (${s.studentFirstName})` : ""}`
                                : s.studentFirstName || t.studentSectionTitleSingle,
                              termLabelFor(locale, s.startTerm),
                            )}
                      </p>
                    ) : null}
                  </div>
                  <div id={`student-${i}-period`} className="min-w-0">
                  <SelectField
                    label={t.period}
                    required
                    value={s.period}
                    onChange={(v) =>
                      updateStudent(i, {
                        period: v as PeriodId,
                        endDate: isShortPeriod(v as PeriodId) ? s.endDate : "",
                      })
                    }
                    options={periodOptions}
                    placeholder={t.choosePeriod}
                    hint={t.periodHint}
                  />
                  </div>
                </div>

                <div className="rounded-lg bg-sand p-5 text-sm">
                  {isFilledBirth(s) ? (
                    info.placement?.grade ? (
                      <p>
                        <span className="font-medium">
                          {t.suggestPrefix(info.placement.label, termLabelFor(locale, s.startTerm))}
                        </span>
                      </p>
                    ) : (
                      <p>{info.placement?.label}</p>
                    )
                  ) : (
                    <p className="text-muted-foreground">
                      {t.fillBirthDateForSuggestion}
                    </p>
                  )}
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div id={`student-${i}-grade`} className="min-w-0">
                  <SelectField
                    label={t.gradeLevel}
                    required
                    value={info.grade}
                    onChange={(v) => updateStudent(i, { grade: v, gradeTouched: true })}
                    options={gradeOptionsForStudent}
                    placeholder={
                      isFilledBirth(s) ? t.chooseLevel : t.fillBirthDateAbove
                    }
                    disabled={!isFilledBirth(s)}
                    error={errors[`students.${i}.grade`]}
                    hint={t.gradeHint}
                  />
                  </div>
                  <Field
                    label={isShortPeriod(s.period) ? t.shortStartFrom : t.startDate}
                    required={isShortPeriod(s.period)}
                    error={info.startDateError || errors[`students.${i}.startDate`]}
                    hint={
                      termStartPassed
                        ? t.earliestStartNotice(earliestStart)
                        : !isShortPeriod(s.period) && !s.customStartDate
                          ? t.termStartsAuto
                          : term
                            ? t.chooseSchoolDayBetween(startFloor, termLast)
                            : t.schoolConfirmsStartDate
                    }
                  >
                    {!isShortPeriod(s.period) && !s.customStartDate ? (
                      <button
                        type="button"
                        className="grid min-h-11 w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-border bg-sand px-3 py-2 text-left text-sm transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => updateStudent(i, { customStartDate: true })}
                      >
                        <span className="min-w-0 whitespace-normal">
                          {termStartPassed
                            ? `${earliestStart} (${asapLabelText})`
                            : termFirst
                              ? `${termFirst}${t.termStartSuffix}`
                              : t.confirmedBySchool}
                        </span>
                        <span className="shrink-0 whitespace-nowrap text-xs font-medium underline decoration-muted-foreground underline-offset-4">
                          {t.chooseOtherDate}
                        </span>
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <DateField
                          defaultOpen={!isShortPeriod(s.period)}
                          value={s.startDate}
                          min={startFloor}
                          max={termLast}
                          locale={locale}
                          isDisabledDay={(iso) =>
                            !checkStartDate(s.startTerm, iso, info.program ?? "").ok
                          }
                          error={Boolean(info.startDateError || errors[`students.${i}.startDate`])}
                          onChange={(iso) => updateStudent(i, { startDate: iso })}
                        />
                        {!isShortPeriod(s.period) ? (
                          <button
                            type="button"
                            className="h-11 shrink-0 rounded-md border border-border px-3 text-xs font-medium transition-colors hover:border-primary"
                            onClick={() =>
                              updateStudent(i, {
                                customStartDate: false,
                                startDate: startFloor,
                              })
                            }
                          >
                            {termStartPassed ? asapLabelText : t.termStart}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </div>


                {isShortPeriod(s.period) ? (
                  <div className="grid gap-5 sm:grid-cols-2">
                    <SelectField
                      label={s.period === "short_week" ? t.shortWeeksCount : t.shortMonthsCount}
                      required
                      value={s.shortCount}
                      onChange={(v) => updateStudent(i, { shortCount: v })}
                      options={
                        s.period === "short_week"
                          ? [
                              { value: "1", label: t.weeksOption(1) },
                              { value: "2", label: t.weeksOption(2) },
                            ]
                          : [
                              { value: "1", label: t.monthsOption(1) },
                              { value: "2", label: t.monthsOption(2) },
                            ]
                      }
                      error={info.shortError || errors[`students.${i}.endDate`]}
                      hint={s.period === "short_week" ? t.shortWeekHint : t.shortMonthHint}
                    />
                    <Field label={t.shortEndDate} hint={t.shortEndDateHint}>
                      <div className="flex h-11 items-center rounded-md border border-border bg-sand px-3 text-sm">
                        {info.shortEndDate
                          ? `${s.startDate} – ${info.shortEndDate}`
                          : t.shortEndDatePending}
                      </div>
                    </Field>
                  </div>
                ) : null}


                {info.afterschool.allowed || info.academy.allowed || info.padel.allowed ? (
                <div className="grid gap-3 sm:grid-cols-1">
                  {info.afterschool.allowed ? (
                  <CheckboxField
                    checked={s.wantsAfterschool}
                    onChange={(v) => updateStudent(i, { wantsAfterschool: v })}
                    tooltip={t.wantsAfterschoolHint}
                    hint={`${t.wantsAfterschoolHint} ${t.afterschoolDiscountHint}`}
                    learnMore={
                      <LearnMoreButton label={t.learnMore} onClick={() => setInfoTopic({ topic: "afterschool", term: s.startTerm })} />
                    }
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      {t.wantsAfterschool}
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium tracking-wide text-primary">
                        <BadgePercent className="size-3.5" aria-hidden="true" />
                        {t.afterschoolDiscountBadge}
                      </span>
                    </span>
                  </CheckboxField>
                  ) : null}
                  {info.academy.allowed ? (
                  <CheckboxField
                    checked={s.wantsFootballAcademy}
                    onChange={(v) => updateStudent(i, { wantsFootballAcademy: v })}
                    hint={t.wantsFootballAcademyHint}
                    learnMore={
                      <LearnMoreButton label={t.learnMore} onClick={() => setInfoTopic({ topic: "football", term: s.startTerm })} />
                    }
                  >
                    {t.wantsFootballAcademy}
                  </CheckboxField>
                  ) : null}

                  {info.padel.allowed ? (
                  <CheckboxField
                    checked={s.wantsPadelTennis}
                    onChange={(v) => updateStudent(i, { wantsPadelTennis: v })}
                    hint={t.wantsPadelTennisHint}
                    learnMore={
                      <LearnMoreButton label={t.learnMore} onClick={() => setInfoTopic({ topic: "padel", term: s.startTerm })} />
                    }
                  >
                    {t.wantsPadelTennis}
                  </CheckboxField>
                  ) : null}
                </div>
                ) : null}


              </Section>
            </div>
          );
        })}

        {students.length < 6 ? (
          <AddRowButton
            label={t.addAnotherStudent}
            onClick={() => setStudents((list) => [...list, { ...emptyStudent }])}
          />
        ) : null}

        <Section
          title={students.length > 1 ? t.guardianSectionTitleMulti : t.guardianSectionTitleSingle}
          description={t.guardianSectionDescription}
        >
          {guardians.map((g, i) => {
          const gHasNationality = Boolean(g.nationality.trim());
          const gPassportKey = `guardian-${i}`;
          const gShowPassportCountry =
            showPassportCountry[gPassportKey] === true ||
            (Boolean(g.passportCountry) && g.passportCountry !== g.nationality) ||
            (g.noIdNumber && !g.passportCountry.trim());
          return (
            <div key={i} id={`guardian-${i}`} className="space-y-5 border-t border-border pt-5 first:border-0 first:pt-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{t.guardianLabel(i + 1)}</p>
                {i > 0 ? (
                  <button
                    type="button"
                    className="text-xs link-quiet"
                    onClick={() => setPendingRemoval({ kind: "guardian", index: i })}
                  >
                    {t.remove}
                  </button>
                ) : null}
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  label={t.firstName}
                  required
                  value={g.firstName}
                  onChange={(v) => updateGuardian(i, { firstName: v })}
                  error={errors[`guardians.${i}.firstName`]}
                />
                <TextField
                  label={t.lastName}
                  required
                  value={g.lastName}
                  onChange={(v) => updateGuardian(i, { lastName: v })}
                  error={errors[`guardians.${i}.lastName`]}
                />
                <TextField
                  label={t.email}
                  required
                  type="email"
                  inputMode="email"
                  value={g.email}
                  onChange={(v) => updateGuardian(i, { email: v })}
                  error={errors[`guardians.${i}.email`]}
                />
                <PhoneField
                  label={t.phone}
                  required
                  value={g.phone}
                  onChange={(v) => updateGuardian(i, { phone: v })}
                  error={errors[`guardians.${i}.phone`]}
                />
                <SelectField
                  label={t.relationToStudent}
                  required
                  value={g.relation}
                  onChange={(v) => updateGuardian(i, { relation: v })}
                  options={relationOptions}
                  placeholder={t.chooseRelation}
                  error={errors[`guardians.${i}.relation`]}
                />
                <ComboboxField
                  label={t.nationality}
                  required
                  value={g.nationality}
                  onChange={(v) => updateGuardian(i, { nationality: v, idNumber: "" })}
                  options={nationalityOptions}
                  placeholder={t.chooseNationality}
                  searchPlaceholder={t.searchCountry}
                  hint={t.guardianNationalityHint}
                  error={errors[`guardians.${i}.nationality`]}
                />
                {gHasNationality && !g.noIdNumber ? (
                  <TextField
                    label={idLabelFor(locale, g.nationality)}
                    required
                    labelTooltip={
                      idTypeForNationality(g.nationality) === "se"
                        ? t.swedishPersonalNumberHint
                        : idHintForNationality(g.nationality, locale)
                    }
                    placeholder={idPlaceholderForNationality(g.nationality)}
                    value={g.idNumber}
                    inputMode={
                      idTypeForNationality(g.nationality) === "se" ? "numeric" : "text"
                    }
                    onChange={(v) =>
                      updateGuardian(i, {
                        idNumber:
                          idTypeForNationality(g.nationality) === "se"
                            ? sanitizeSwedishPersonalNumberInput(v)
                            : v,
                      })
                    }
                    onBlur={() => {
                      if (idTypeForNationality(g.nationality) !== "se") return;
                      const formatted = formatSwedishPersonalNumber(g.idNumber);
                      if (formatted !== g.idNumber) updateGuardian(i, { idNumber: formatted });
                      setTouchedIds((prev) => ({ ...prev, [`guardian-${i}`]: true }));
                    }}
                    error={
                      (g.idNumber.trim() &&
                      (idTypeForNationality(g.nationality) !== "se" ||
                        touchedIds[`guardian-${i}`] === true)
                        ? (validateIdNumber(
                            idTypeForNationality(g.nationality),
                            g.idNumber,
                            undefined,
                            locale,
                          ).error ?? "")
                        : "") || errors[`guardians.${i}.idNumber`]
                    }

                  />
                ) : null}

              </div>
              {gHasNationality ? (
                <CheckboxField
                  checked={g.noIdNumber}
                  onChange={(v) =>
                    updateGuardian(i, {
                      noIdNumber: v,
                      idNumber: "",
                      ...(v && !g.passportCountry ? { passportCountry: g.nationality } : {}),
                    })
                  }
                  compact
                >
                  {t.guardianMissingIdCheckbox(missingIdLabel(locale, g.nationality))}
                </CheckboxField>
              ) : null}
              {gHasNationality && g.noIdNumber ? (
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-xs leading-relaxed text-muted-foreground">{t.passportIntro}</p>
                  <div className="mt-4 grid gap-5 sm:grid-cols-2">
                    <TextField
                      label={t.passportNumber}
                      required
                      value={g.passportNumber}
                      onChange={(v) => updateGuardian(i, { passportNumber: formatPassportNumber(v) })}
                      labelTooltip={t.passportNumberHint}
                      error={
                        errors[`guardians.${i}.passportNumber`] ??
                        (validatePassportNumber(g.passportCountry, g.passportNumber, locale).error ||
                          undefined)
                      }
                      warning={
                        validatePassportNumber(g.passportCountry, g.passportNumber, locale).hint ||
                        undefined
                      }
                    />

                    {gShowPassportCountry ? (
                      <ComboboxField
                        label={t.passportCountry}
                        required
                        value={g.passportCountry}
                        onChange={(v) => updateGuardian(i, { passportCountry: v })}
                        options={nationalityOptions}
                        placeholder={t.passportCountryPlaceholder}
                        searchPlaceholder={t.searchCountry}
                        error={errors[`guardians.${i}.passportCountry`]}
                      />
                    ) : (
                      <div className="flex flex-col justify-center gap-1">
                        <p className="text-sm">
                          {t.passportIssuedIn(countryNameIn(g.nationality, locale))}
                        </p>
                        <button
                          type="button"
                          className="self-start text-xs font-medium text-primary link-quiet hover:opacity-80"
                          onClick={() =>
                            setShowPassportCountry((prev) => ({ ...prev, [gPassportKey]: true }))
                          }
                        >
                          {t.passportOtherCountry}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              {students.length > 1 ? (
                <SelectField
                  label={t.addressForGuardian}
                  value={g.sameAddressAs}
                  onChange={(v) => updateGuardian(i, { sameAddressAs: v })}
                  options={[
                    ...students.map((st, si) => ({
                      value: String(si),
                      label: t.sameAddressAsStudentN(st.studentFirstName || t.studentFallback(si + 1)),
                    })),
                    { value: "own", label: t.ownAddress },
                  ]}
                />
              ) : (
                <CheckboxField
                  checked={g.sameAddressAs !== "own"}
                  onChange={(v) => updateGuardian(i, { sameAddressAs: v ? "0" : "own" })}
                  hint={t.sameAddressAsStudentHint}
                >
                  {t.sameAddressAsStudent}
                </CheckboxField>
              )}
              {g.sameAddressAs !== "own" ? (
                <AddressSummary
                  values={resolvedAddress(Number(g.sameAddressAs) || 0)}
                  title={t.usedAddress}
                  emptyText={t.addressNotFilledYet}
                />
              ) : null}
              {g.sameAddressAs === "own" ? (
                <div className="rounded-lg bg-sand p-5">
                  <AddressFields
                    values={{
                      address: g.address,
                      postalCode: g.postalCode,
                      city: g.city,
                      region: g.region,
                      country: g.country,
                    }}
                    locale={locale}
                    onChange={(patch) => updateGuardian(i, patch)}
                    errors={{
                      address: errors[`guardians.${i}.address`],
                      postalCode: errors[`guardians.${i}.postalCode`],
                      city: errors[`guardians.${i}.city`],
                      region: errors[`guardians.${i}.region`],
                      country: errors[`guardians.${i}.country`],
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
          })}
          {guardians.length < 2 ? (
            <AddRowButton
              label={t.addGuardian}
              onClick={() => setGuardians((list) => [...list, { ...emptyGuardian }])}
            />
          ) : null}
        </Section>

        {/* På mobil visas avgiften bara här, med egna Visa/Dölj – sticky-panelen är enbart för större skärmar. */}
        <div className="lg:hidden">
          <FeeSummary perStudent={perStudent} students={students} grandTotal={grandTotal} t={t} feesHref={feesHref} locale={locale} onOpenPage={openPage} />
        </div>


        <Section title={t.relocationSectionTitle}>
          <CheckboxField
            checked={wantsConsultation && hasFullTimeStudent}
            disabled={!hasFullTimeStudent}
            onChange={setWantsConsultation}
            hint={hasFullTimeStudent ? t.relocationHint : t.relocationFullTimeOnly}
            learnMore={
              <LearnMoreButton label={t.relocationReadMore} onClick={() => setRelocationInfoOpen(true)} />
            }
          >
            {t.relocationCheckbox}
          </CheckboxField>
        </Section>

        <Section title={t.consentSectionTitle}>
          <div id="enrollment-consent">
          <CheckboxField
            checked={consent}
            onChange={(v) => {
              setConsent(v);
              if (v) {
                setErrors((prev) => {
                  if (!("consent" in prev)) return prev;
                  const next = { ...prev };
                  delete next["consent"];
                  return next;
                });
              }
            }}
            error={errors["consent"]}
          >
            {t.consentPrefix}{" "}
            <span className="block pt-1 text-sm text-muted-foreground">
            <button
              type="button"
              className="text-primary link-quiet"
              onClick={() => openPage(privacyHref, t.privacyPolicy)}
            >
              {t.privacyPolicy}
            </button>{" "}
            {t.and}{" "}
            <button
              type="button"
              className="text-primary link-quiet"
              onClick={() => openPage(termsHref, t.terms)}
            >
              {t.terms}
            </button>
            .
            </span>
          </CheckboxField>
          </div>
          {hasHealthInformation ? (
            <div id="enrollment-health-consent">
              <CheckboxField
                checked={healthConsent}
                onChange={(value) => {
                  setHealthConsent(value);
                  if (value) setErrors((previous) => {
                    const next = { ...previous };
                    delete next["healthConsent"];
                    return next;
                  });
                }}
                error={errors["healthConsent"]}
              >
                {t.healthConsent}
              </CheckboxField>
            </div>
          ) : null}

          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />

          <button
            type="submit"
            disabled={sending}
            className="rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {sending ? t.submitting : t.submit}
          </button>
          <SuperAdminSkip onSkip={() => setDone(true)} />
          <p className="text-xs text-muted-foreground">
            {t.notBindingNote}
          </p>
        </Section>
        {/* Plats för den fasta mobilindikatorn så den inte täcker skicka-knappen. */}
        <div className="h-20 lg:hidden" aria-hidden="true" />
      </div>

      <aside className="hidden self-start lg:sticky lg:top-24 lg:block lg:max-h-[calc(100dvh-7rem)]">
        <FeeSummary perStudent={perStudent} students={students} grandTotal={grandTotal} t={t} feesHref={feesHref} locale={locale} onOpenPage={openPage} />
      </aside>

      {!done && progress.pct > 0 ? (
        <MobileProgressBar progress={progress} grandTotal={grandTotal} pending={feePending} t={t} />
      ) : null}

      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingRemoval?.kind === "guardian"
                ? t.confirmRemoveGuardianTitle
                : t.confirmRemoveStudentTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval?.kind === "guardian"
                ? t.confirmRemoveGuardianBody
                : t.confirmRemoveStudentBody}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => {
                if (!pendingRemoval) return;
                const { kind, index } = pendingRemoval;
                if (kind === "student") {
                  setStudents((list) => list.filter((_, idx) => idx !== index));
                } else {
                  setGuardians((list) => list.filter((_, idx) => idx !== index));
                }
                setPendingRemoval(null);
              }}
            >
              {pendingRemoval?.kind === "guardian" ? t.confirmRemove : t.removeStudent}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.draftDiscardConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t.draftDiscardConfirmBody}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => {
                try {
                  window.localStorage.removeItem(DRAFT_KEY);
                } catch {
                  /* noop */
                }
                draftId.current = newDraftId();
                lastRemote.current = "";
                // Tala om för chipet "påbörjad anmälan" att utkastet är borta.
                window.dispatchEvent(new CustomEvent("sis-enrollment-draft-changed"));
                setStudents([{ ...emptyStudent }]);
                setGuardians([{ ...emptyGuardian }]);
                setConsent(false);
                setHealthConsent(false);
                setWantsConsultation(false);
                setErrors({});
                setSavedAt(null);
                setShowRestoredDraft(false);
                setConfirmDiscard(false);
                toast.info(t.draftDiscarded, { id: "enrollment" });
              }}
            >
              {t.draftDiscard}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={infoTopic !== null} onOpenChange={(open) => !open && setInfoTopic(null)}>
        <DialogContent className="max-w-lg">
          {infoTopic ? (
            <>
              <DialogHeader>
                <DialogTitle>{infoContent(t, infoTopic.topic).title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                {infoContent(t, infoTopic.topic).body.map((p) => (
                  <p key={p}>{p}</p>
                ))}
                {/* Priset hämtas alltid från aktuell prislista för vald termin. */}
                <p className="rounded-lg bg-sand p-3 text-foreground">
                  {activityPriceText(infoTopic.topic, infoTopic.term, locale)}
                </p>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Flyttgenomgångens "Läs mer" – samma mönster som aktiviteterna ovan. */}
      <Dialog open={relocationInfoOpen} onOpenChange={setRelocationInfoOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.relocationInfo.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
            {t.relocationInfo.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
            <p className="text-xs">{t.relocationInfo.disclaimer}</p>
            <p>
              <a className="text-primary link-quiet" href={relocationHref}>
                {t.relocationFullPage}
              </a>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Interna sidor (integritetspolicy, villkor, prislista) öppnas i modal. */}
      <PageModal
        open={pageModal !== null}
        onOpenChange={(open) => !open && setPageModal(null)}
        href={pageModal?.href ?? ""}
        title={pageModal?.href === feesHref ? (locale === "en" ? "Fees" : locale === "es" ? "Tarifas" : "Avgifter") : pageModal?.title ?? ""}
        newTabLabel={t.openInNewTab}
        preserveRightColumn
      >
        {pageModal?.href === feesHref ? (
          <section className="h-full min-h-0">
            <FeesByTerm locale={locale} embedded {...(sitePricing ? { initialData: sitePricing } : {})} />
          </section>
        ) : pageModal?.href === privacyHref ? (
          <article className="policy-body px-5 py-6 text-sm leading-relaxed text-foreground/85 sm:px-8 [&_a]:font-medium [&_a]:text-primary [&_h2]:pt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_li]:leading-relaxed [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
            <PrivacyPolicyContent locale={locale} showIntro />
          </article>
        ) : pageModal?.href === termsHref ? (
          <article className="policy-body px-5 py-6 text-sm leading-relaxed text-foreground/85 sm:px-8 [&_a]:font-medium [&_a]:text-primary [&_h2]:pt-4 [&_h2]:text-xl [&_h2]:font-semibold">
            <TermsPolicyContent locale={locale} showIntro />
          </article>
        ) : undefined}
      </PageModal>
    </form>
    </div>
  );

}

function infoContent(
  t: (typeof ENROLLMENT_TEXT)[EnrollmentLocale],
  topic: "afterschool" | "football" | "padel",
): { title: string; body: readonly string[] } {
  // Fritids: erbjudandet står först så det syns direkt i "Läs mer".
  if (topic === "afterschool")
    return { title: t.infoAfterschool.title, body: [t.afterschoolOffer, ...t.infoAfterschool.body] };
  if (topic === "football") return t.infoFootball;
  return t.infoPadel;
}

/**
 * Aktuellt pris för vald termin, hämtat ur prislistan i stället för fast text.
 * Saknas priset för terminen sägs det rakt ut i stället för att gissa.
 */
function activityPriceText(
  topic: "afterschool" | "football" | "padel",
  termId: string,
  locale: EnrollmentLocale,
): string {
  const label = termLabelFor(locale, termId as TermId);
  const pending =
    locale === "en"
      ? `The price for ${label} is not set yet – we confirm it before anything is booked.`
      : locale === "es"
        ? `El precio para ${label} aún no está fijado: lo confirmamos antes de reservar nada.`
        : `Priset för ${label} är inte beslutat ännu – vi bekräftar det innan något bokas.`;

  if (topic === "afterschool") {
    const prices = afterschoolPricesForTerm(termId);
    if (!prices) return pending;
    const parts: string[] = [];
    if (prices.term) {
      parts.push(
        locale === "en"
          ? `${formatEur(prices.term)} per term`
          : locale === "es"
            ? `${formatEur(prices.term)} por semestre`
            : `${formatEur(prices.term)} per termin`,
      );
    }
    if (prices.year) {
      parts.push(
        locale === "en"
          ? `${formatEur(prices.year)} for a full year`
          : locale === "es"
            ? `${formatEur(prices.year)} por curso completo`
            : `${formatEur(prices.year)} för helår`,
      );
    }
    if (prices.shortWeek) {
      parts.push(
        locale === "en"
          ? `${formatEur(prices.shortWeek)} per week`
          : locale === "es"
            ? `${formatEur(prices.shortWeek)} por semana`
            : `${formatEur(prices.shortWeek)} per vecka`,
      );
    }
    if (prices.shortMonth) {
      parts.push(
        locale === "en"
          ? `${formatEur(prices.shortMonth)} per month`
          : locale === "es"
            ? `${formatEur(prices.shortMonth)} por mes`
            : `${formatEur(prices.shortMonth)} per månad`,
      );
    }
    if (parts.length === 0) return pending;
    return locale === "en"
      ? `Price for ${label}: ${parts.join(", ")}.`
      : locale === "es"
        ? `Precio para ${label}: ${parts.join(", ")}.`
        : `Pris för ${label}: ${parts.join(", ")}.`;
  }

  const fee = activityFeeForTerm(topic === "football" ? "football" : "padel", termId);
  if (!fee) return pending;
  return locale === "en"
    ? `Price for ${label}: ${formatEur(fee)} per term.`
    : locale === "es"
      ? `Precio para ${label}: ${formatEur(fee)} por semestre.`
      : `Pris för ${label}: ${formatEur(fee)} per termin.`;
}

function LearnMoreButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex min-h-11 items-center py-2 text-xs font-medium text-primary link-quiet hover:opacity-80"
    >
      {label}
    </button>
  );
}


type FeeSummaryProps = {
  perStudent: { fees: ReturnType<typeof calculateFees> | null; academy: { allowed: boolean; reason?: string } }[];
  students: Student[];
  grandTotal: number;
  t: (typeof ENROLLMENT_TEXT)[EnrollmentLocale];
  feesHref: string;
  locale: EnrollmentLocale;
  /** Öppnar en intern sida i modal på plats (ingen navigation bort från formuläret). */
  onOpenPage: (href: string, title: string) => void;
};

/**
 * Syskonrabatten enligt skolsystemets regler: rabatten läggs alltid på det
 * barn som har den lägsta skolavgiften. Anmäls för få barn visas i stället en
 * upplysning om från vilket barn rabatten börjar gälla.
 */
function SiblingDiscountNote({
  childrenCount,
  note,
  locale,
}: {
  childrenCount: number;
  note: string;
  locale: EnrollmentLocale;
}) {
  const from = siblingDiscountFromChild();
  if (from <= 0) return null;
  const percent = percentForChildNumber(from);
  const cheapest =
    locale === "en"
      ? "The discount is always applied to the lowest school fee in the family."
      : locale === "es"
        ? "El descuento se aplica siempre a la cuota escolar más baja de la familia."
        : "Rabatten läggs alltid på familjens lägsta skolavgift.";
  if (childrenCount >= from) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        {note} {cheapest}
      </p>
    );
  }
  const upsell =
    locale === "en"
      ? `Family discount starts from child number ${from}: ${percent}% off the lowest school fee.`
      : locale === "es"
        ? `El descuento familiar empieza en el hijo número ${from}: ${percent} % sobre la cuota escolar más baja.`
        : `Familjerabatt gäller från barn nr ${from}: ${percent} % på den lägsta skolavgiften.`;
  return <p className="mt-2 text-xs text-muted-foreground">{upsell}</p>;
}


function FeeSummary({ perStudent, students, grandTotal, t, feesHref, locale, onOpenPage }: FeeSummaryProps) {
  const [feeInfo, setFeeInfo] = useState<{ info: FeeLineInfo; label: string; amount: number; term: string } | null>(
    null,
  );
  // Detaljlistan är ihopfälld som standard – rubrikraden visar totalsumman och
  // en liten Visa/Dölj-knapp längst till höger fäller ut den.
  const [feeOpen, setFeeOpen] = useState(false);
  const hasFees = perStudent.some((p) => p.fees && !p.fees.pricingPending);
  const feeInfoContent =
    feeInfo?.info === "registration"
      ? t.feeInfoRegistration
      : feeInfo?.info === "registration_short"
        ? t.feeInfoRegistrationShort
        : feeInfo?.info === "late"
          ? t.feeInfoLate
          : null;
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-center justify-between gap-3">
        <h3 className="min-w-0 text-lg font-semibold">
          {t.feeSummaryTitle}
          {hasFees ? `: ${formatEur(grandTotal)}` : ""}
        </h3>
        {hasFees ? (
          <button
            type="button"
            onClick={() => setFeeOpen((v) => !v)}
            aria-expanded={feeOpen}
            className="shrink-0 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {feeOpen ? t.feeHide : t.feeShow}
          </button>
        ) : null}
      </div>
      {perStudent.some((p) => p.fees?.pricingPending) ? (
        <div className="mt-4 rounded-lg border border-border bg-sand p-4">
          <p className="text-sm font-medium">
            {perStudent.some((p) => p.fees?.pricingLoadFailed) ? t.feeLoadFailedTitle : t.feePendingTitle}
          </p>
          {perStudent.some((p) => p.fees?.pricingLoadFailed) ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t.feeLoadFailedBody}</p>
          ) : (
            <ul className="mt-1 space-y-1 text-xs leading-relaxed text-muted-foreground">
              {perStudent.map((p, i) =>
                p.fees?.pricingPending ? (
                  <li key={i}>
                    {t.feePendingForTerm(
                      students.length > 1
                        ? `${t.studentSectionTitleMulti(i + 1)}${students[i]?.studentFirstName ? ` (${students[i]!.studentFirstName})` : ""}`
                        : students[i]?.studentFirstName || t.studentSectionTitleSingle,
                      termLabelFor(locale, students[i]!.startTerm),
                    )}
                  </li>
                ) : null,
              )}
            </ul>
          )}
        </div>
      ) : null}
      {!feeOpen && hasFees ? (
        <div className="mt-4 space-y-2 border-t border-border pt-4">
          {perStudent.map((p, i) =>
            p.fees && !p.fees.pricingPending ? (
              <div key={i} className="text-sm">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                  <span className="min-w-0 truncate font-medium">
                    {students[i]?.studentFirstName || t.feeStudentFallback(i + 1)}
                  </span>
                  <span className="shrink-0 font-medium">{formatEur(p.fees.total)}</span>
                </div>
                {(p.fees.discountLines ?? (p.fees.discountLine ? [p.fees.discountLine] : [])).map(
                  (line) => (
                    <div
                      key={line.label}
                      className="mt-1 grid grid-cols-[minmax(0,1fr)_auto] gap-3 text-xs text-primary"
                    >
                      <span className="min-w-0">{line.label}</span>
                      <span className="shrink-0">−{formatEur(Math.abs(line.amount))}</span>
                    </div>
                  ),
                )}
              </div>
            ) : null,
          )}
        </div>
      ) : null}
      {feeOpen && perStudent.some((p) => p.fees && !p.fees.pricingPending) ? (
        <ScrollArea className="mt-4 h-[min(34rem,calc(100dvh-15rem))] border-t border-border pr-4">
          <div className="pb-1 pt-4">
          {perStudent.map((p, i) =>
            p.fees && !p.fees.pricingPending ? (

              <div key={i} className="mt-5 border-t border-border pt-4 first:border-0 first:pt-0">
                <p className="text-sm font-medium">
                  {students[i]?.studentFirstName || t.feeStudentFallback(i + 1)}
                </p>
                <ul className="mt-3 space-y-2 text-sm">
                  {p.fees.lines.map((line, lineIndex) => (
                    <Fragment key={`${line.label}-${lineIndex}`}>
                    <li className="flex justify-between gap-3">
                      {line.info ? (
                        <button
                          type="button"
                           onClick={() => setFeeInfo({ info: line.info!, label: line.label, amount: line.amount, term: students[i]?.startTerm ?? "" })}
                          className="text-left text-muted-foreground underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 transition-colors hover:text-foreground"
                        >
                          {feeLabelIn(line.label, locale)}
                        </button>
                      ) : (
                        <span className="text-muted-foreground">{feeLabelIn(line.label, locale)}</span>
                      )}
                      <span className="whitespace-nowrap">{formatEur(line.amount)}</span>
                    </li>
                    {lineIndex === 0 ? (
                      <li className="mt-1 text-xs text-muted-foreground">
                        {locale === "en" ? "Start" : locale === "es" ? "Inicio" : "Start"} {termLabelFor(locale, students[i]!.startTerm).toLocaleLowerCase(locale === "sv" ? "sv-SE" : locale === "es" ? "es-ES" : "en-GB")}
                      </li>
                    ) : null}
                    </Fragment>
                  ))}
                  {(p.fees.discountLines ?? (p.fees.discountLine ? [p.fees.discountLine] : [])).map(
                    (line) => (
                      <li key={line.label} className="flex justify-between gap-3 text-primary">
                        <span>{line.label}</span>
                        <span className="whitespace-nowrap">
                          −{formatEur(Math.abs(line.amount))}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
                <div className="mt-3 flex justify-between text-sm font-medium">
                  <span>{t.subtotal}</span>
                  <span>{formatEur(p.fees.total)}</span>
                </div>
                {students[i]!.wantsFootballAcademy && !p.academy.allowed ? (
                  <p className="mt-2 text-xs text-muted-foreground">{p.academy.reason}</p>
                ) : null}
              </div>
            ) : null,
          )}
          <div className="mt-5 flex justify-between border-t border-border pt-4 text-base font-semibold">
            <span>{t.total}</span>
            <span>{formatEur(grandTotal)}</span>
          </div>
          <SiblingDiscountNote
            childrenCount={students.length}
            note={t.familyDiscountNote}
            locale={locale}
          />

          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t.feeDisclaimer}</p>
          </div>
        </ScrollArea>
      ) : hasFees || perStudent.some((p) => p.fees?.pricingPending) ? null : (
        <p className="mt-4 text-sm text-muted-foreground">{t.fillBirthAndStart}</p>
      )}
      <p className="mt-4 text-xs text-muted-foreground">
        {t.seeFullPriceList}{" "}
        <button
          type="button"
          className="text-primary link-quiet"
          onClick={() => onOpenPage(feesHref, t.feesPage)}
        >
          {t.feesPage}
        </button>
        .
      </p>

      <Dialog open={feeInfo !== null} onOpenChange={(open) => !open && setFeeInfo(null)}>
        <DialogContent className="max-w-lg">
          {feeInfoContent ? (
            <>
              <DialogHeader>
                <DialogTitle>{feeInfoContent.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                {feeInfo ? (
                  <p className="flex justify-between gap-3 rounded-lg border border-border bg-sand px-3 py-2 text-foreground">
                    <span>{feeInfo.label}</span>
                    <span className="whitespace-nowrap font-medium">{formatEur(feeInfo.amount)}</span>
                  </p>
                ) : null}
                {feeInfoContent.body.map((p) => (
                  <p key={p}>{p}</p>
                ))}
                 {feeInfo?.info === "registration" || feeInfo?.info === "registration_short" ? (
                   <p className="font-medium text-foreground">
                     {registrationDueText(feeInfo.term, locale, feeInfo.info === "registration_short" ? "shortterm" : "fulltime") ?? (
                       locale === "en" ? "As per the payment plan agreed at admission" : locale === "es" ? "Según el plan de pago acordado en la admisión" : "Enligt betalningsplanen vid antagning"
                     )}
                   </p>
                 ) : null}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Fast indikator på mobil som visar hur långt anmälan är ifylld. */
function MobileProgressBar({
  progress,
  grandTotal,
  pending,
  t,
}: {
  progress: { pct: number; next: string };
  grandTotal: number;
  pending: boolean;
  t: (typeof ENROLLMENT_TEXT)[EnrollmentLocale];
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  // Portal till <body> så inget föräldraelement (transform/filter) kan göra
  // att den "fasta" raden följer med i scrollen på iOS.
  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 px-4 py-3 backdrop-blur lg:hidden">
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-medium">{t.progressLabel(progress.pct)}</span>
        <span className="text-muted-foreground">
          {pending ? t.feePendingTitle : grandTotal > 0 ? formatEur(grandTotal) : ""}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${progress.pct}%` }}
        />
      </div>
      <p className="mt-1.5 truncate text-[11px] text-muted-foreground">
        {progress.next ? t.progressNext(progress.next) : t.progressDone}
      </p>
    </div>,
    document.body,
  );
}
